import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Row, Col, Card, Button, Form, Badge, Modal, Alert, Nav, Tab } from 'react-bootstrap';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
  People,
  FileText,
  PencilSquare,
  ArrowClockwise,
  ExclamationTriangle,
  LightningCharge,
  Robot,
  Trophy,
} from 'react-bootstrap-icons';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useBranchAPI, useFilterParams } from '../../hooks';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { shiftAPI, employeeAPI, shiftTemplateAPI, shiftPreferenceAPI } from '../../services';
import { toast } from 'react-toastify';
import NewHireCashierBadge from '../../components/shift/NewHireCashierBadge';
import { getErrorMessage } from '../../utils/apiClient';
import moment from 'moment';
import ShiftDayDetailModal from '../../components/modals/ShiftDayDetailModal';
import EditApprovedShiftModal from '../../components/modals/EditApprovedShiftModal';
import EditProposedShiftModal from '../../components/modals/EditProposedShiftModal';
import { useRejectShiftReason } from '../../components/modals/RejectShiftReasonModal';
import PaginationComponent from '../../components/PaginationComponent';
import { getUserFromLocalStorage } from '../../utils/auth';
import { getShortName } from '../../utils/employeeName';
import OvertimeApprovalTab from '../../components/admin-shift-review/OvertimeApprovalTab';
// #133908 — bộ điều khiển yếu tố đề xuất của bot (bật/tắt + kéo-thả ưu tiên).
import BotFactorControlPanel from '../../components/admin-shift-review/BotFactorControlPanel';
import ShiftLeaderboardTab from '../../components/admin-shift-review/ShiftLeaderboardTab';
import { WeekCloseModal } from '../../components/admin-shift-review/WeekClosePanel';
import { useAuth } from '../../context/AuthProvider';

// Mock data - sẽ được thay thế bằng API calls
const initialShifts = [];
const initialTemplates = [];
const initialEmployees = [];

// const slotColors = [
//   'bg-primary',
//   'bg-success',
//   'bg-warning',
//   'bg-info',
//   'bg-danger',
//   'bg-secondary',
//   'bg-dark',
//   'bg-light text-dark',
// ];

const SHIFT_TABS = ['calendar', 'overtime', 'low-registration', 'empty-slots', 'bot-review', 'leaderboard'];
const SHIFT_VIEWS = ['week', 'month'];
// #125045 — nút đổi CHẾ ĐỘ HIỂN THỊ lịch ca theo loại ca: tất cả / ca nhân viên đã đăng ký
// (đang chờ duyệt) / ca đã được duyệt. Dùng CHUNG nguồn lọc `status` sẵn có của trang nên không
// đẻ thêm tầng lọc, vẫn giữ deep-link `?status=` như cũ.
const SHIFT_TYPE_VIEW_MODES = [
  { value: '', label: 'Tất cả', title: 'Hiển thị mọi loại ca' },
  { value: 'PENDING', label: 'Ca đăng ký', title: 'Chỉ hiển thị ca nhân viên đã đăng ký (đang chờ duyệt)' },
  { value: 'APPROVED', label: 'Ca đã duyệt', title: 'Chỉ hiển thị ca đã được duyệt' },
];
const SHIFT_STATUSES = [
  'PENDING',
  'PROPOSED_BOT',
  'APPROVED',
  'APPROVE_BOT',
  'IN_PROGRESS',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
];
// #125027 — bộ trạng thái được tính là "nhân viên ĐÃ ĐĂNG KÝ ca", khớp NGUỒN DUY NHẤT ở máy chủ
// (`on-coffee-server/src/modules/shift-preference/shift-preference-week.constants.ts`:
// `SHIFT_DA_DANG_KY_STATUSES` + phần riêng của bảng xếp hạng). Khối đếm "đăng ký bao nhiêu ca"
// trên màn Quản lý ca phải dùng đúng bộ này để không lệch với màn "Xếp hạng đóng góp ca" (#212635).
const REGISTERED_SHIFT_STATUSES = [
  'PENDING',
  'PROPOSED_BOT',
  'APPROVED',
  'APPROVE_BOT',
  'IN_PROGRESS',
  'COMPLETED',
  'COMPLETED_WAIT_APPROVE',
  'PENDING_TRANSFER',
];
const isShiftIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && moment(v, 'YYYY-MM-DD', true).isValid();

// #120936 — deep-link màn quản lý ca. `branch_id` do BranchContext quản chung.
// `from` là ngày MỐC của cửa sổ lịch đang xem; `view` cho biết cửa sổ là tuần hay tháng
// (trang chỉ có 2 chế độ này, không có ô chọn khoảng tự do nên không có `to` riêng).
const SHIFT_FILTER_SPEC = {
  tab: { default: 'calendar', valid: (v) => SHIFT_TABS.includes(v) },
  view: { default: 'week', valid: (v) => SHIFT_VIEWS.includes(v) },
  from: {
    default: () => moment().startOf('isoWeek').format('YYYY-MM-DD'),
    valid: isShiftIsoDate,
  },
  status: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => SHIFT_STATUSES.includes(v.toUpperCase()),
  },
  preferred: { default: false, parse: (v) => v === 'true', valid: (v) => v === 'true' || v === 'false' },
};

export default function AdminShiftManagement() {
  const { selectedBranch } = useBranch();
  const { withBranch } = useBranchAPI();

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] = useFilterParams(SHIFT_FILTER_SPEC);
  const currentDate = useMemo(() => moment(urlFilters.from, 'YYYY-MM-DD').toDate(), [urlFilters.from]);
  const viewMode = urlFilters.view;
  const statusFilter = urlFilters.status;
  const preferredFilter = urlFilters.preferred;
  const activeTab = urlFilters.tab;

  const setCurrentDate = (date) => setUrlFilters({ from: moment(date).format('YYYY-MM-DD') });
  const setViewMode = (view) => setUrlFilters({ view });
  const setStatusFilter = (status) => setUrlFilters({ status });
  const setPreferredFilter = (next) =>
    setUrlFilters((prev) => ({ preferred: typeof next === 'function' ? next(prev.preferred) : next }));
  const setActiveTab = (tab) => setUrlFilters({ tab });

  const [selectedDate, setSelectedDate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // #201334 — từ chối ca bắt buộc nhập lý do (hộp hỏi dùng chung cho mọi nút "Từ chối" ca).
  const { askRejectReason, rejectReasonModal } = useRejectShiftReason();
  const [branchName, setBranchName] = useState('');

  // State cho dữ liệu
  const [shifts, setShifts] = useState(initialShifts);
  const [templates, setTemplates] = useState(initialTemplates);
  const [employees, setEmployees] = useState(initialEmployees);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [showEditApprovedModal, setShowEditApprovedModal] = useState(false);
  const [editingApprovedShift, setEditingApprovedShift] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageCache, setCoverageCache] = useState({});
  const [lowRegistrationShifts, setLowRegistrationShifts] = useState([]);
  const [lowRegistrationLoading, setLowRegistrationLoading] = useState(false);
  const [lowRegistrationPage, setLowRegistrationPage] = useState(1);
  const [lowRegistrationLimit] = useState(10);
  const [lowRegistrationTotal, setLowRegistrationTotal] = useState(0);
  const [lowRegistrationPeriod, setLowRegistrationPeriod] = useState('current-month'); // 'current-month', 'next-month', 'next-week'
  const [emptySlots, setEmptySlots] = useState([]);
  const [emptySlotsLoading, setEmptySlotsLoading] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [selectedSlotForBoost, setSelectedSlotForBoost] = useState(null);
  const [boostRewardRate, setBoostRewardRate] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(moment().add(1, 'week').startOf('isoWeek')); // Mặc định tuần tới

  // Auto approve
  const [autoApproveLoading, setAutoApproveLoading] = useState(false);
  // Nút "Chốt tuần" (chỉ ADMIN): null = đóng; '' = tuần sau; 'YYYY-MM-DD' = Thứ 2 của tuần cần chốt.
  const { user: authUser } = useAuth();
  const isAdminUser = authUser?.role === 'ADMIN';
  const [closeWeekTarget, setCloseWeekTarget] = useState(null);
  const [botShifts, setBotShifts] = useState([]);
  // #125027 — số ca mỗi nhân viên ĐÃ ĐĂNG KÝ trong tuần đang xem của đề xuất bot, tra theo
  // employee_id. Nạp kèm lô đề xuất để hiện "đề xuất X ca · đăng ký Y ca" ngay dưới các thẻ số.
  const [registeredCountsByEmployee, setRegisteredCountsByEmployee] = useState({});
  // #180331 — đề xuất của bot dùng MẪU CA ĐÃ BỊ VÔ HIỆU HOÁ / đã đổi ngày áp dụng: KHÔNG còn
  // đường nào duyệt được. Tách khỏi danh sách duyệt (để "Duyệt tất cả" không kẹt) và báo riêng.
  const [staleBotShifts, setStaleBotShifts] = useState([]);
  const [botShiftsLoading, setBotShiftsLoading] = useState(false);
  // #015110 — bảng ca đề xuất dạng LỊCH TUẦN: tuần đang xem + kiểu xem + modal sửa tay.
  // Trước đây tab này khoá cứng "tuần tới"; sếp cần chọn tuần để xem đúng lô đề xuất muốn duyệt.
  const [botWeekStart, setBotWeekStart] = useState(moment().add(1, 'week').startOf('isoWeek'));
  const [botViewMode, setBotViewMode] = useState('week');
  // #180848 — khối "Bot Review Multi-option" nay dùng chung cho cả 2 kiểu xem; mặc định MỞ.
  const [botOptionsCollapsed, setBotOptionsCollapsed] = useState(false);
  const [showEditProposedModal, setShowEditProposedModal] = useState(false);
  const [editingProposedShift, setEditingProposedShift] = useState(null);
  // #001848 — ĐĂNG KÝ ca ưu tiên của cả chi nhánh (APPROVED), tải MỘT lần rồi tra tại chỗ:
  // mỗi ca đề xuất cần biết "còn ai cùng đăng ký ca này" mà gọi /shifts/available-employees cho
  // từng ca thì một tuần đã là vài chục lượt gọi.
  const [branchPreferences, setBranchPreferences] = useState([]);
  const [quickSwapShiftId, setQuickSwapShiftId] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [botProposals, setBotProposals] = useState([]);
  // Meta xếp hạng doanh thu của bot (#111404): cấu hình + bảng hạng ca / hạng NV
  const [proposalRevenueMeta, setProposalRevenueMeta] = useState(null);
  // #133908 — câu giải thích khi bộ yếu tố không sinh đủ 5 phương án (bật thiếu yếu tố, hoặc
  // ràng buộc cứng làm các phương án ra kết quả y hệt nhau).
  const [proposalShortageReason, setProposalShortageReason] = useState(null);
  const [selectedProposalId, setSelectedProposalId] = useState('');
  const [proposalItemsOverride, setProposalItemsOverride] = useState({});
  const [proposalLoading, setProposalLoading] = useState(false);
  const [applyProposalLoading, setApplyProposalLoading] = useState(false);
  // #182918 — kết quả lần áp dụng gần nhất (khung xanh dưới tiêu đề khối đề xuất): tên đề xuất,
  // giờ áp dụng, số ca đã chuyển và số còn lại sau áp dụng. Xoá khi chạm đề xuất khác / đổi
  // chi nhánh / đổi tuần, và (#191002) ngay khi có thao tác nào làm đổi lô ca đề xuất sau áp dụng:
  // Revoke All (cả 2 nút), duyệt cả tuần, Auto Approve, đổi người / sửa / duyệt / revoke / từ chối
  // lẻ 1 ca — khung chỉ được hiện khi số trong đó còn đúng với trạng thái ngay sau áp dụng.
  const [appliedProposalResult, setAppliedProposalResult] = useState(null);
  // #182918 — lần chạm trước trên nút đề xuất ({ id, at }) để tự bắt chạm đúp bằng mốc giờ
  // (không dựa vào sự kiện dblclick — iPhone không bắn ổn định).
  const lastProposalTapRef = useRef({ id: null, at: 0 });
  const [floorSuggestionLoading, setFloorSuggestionLoading] = useState(false);
  const [floorSuggestionData, setFloorSuggestionData] = useState(null);

  // State cho bulk creation
  const [bulkFormData, setBulkFormData] = useState({
    templateId: '',
    startDate: '',
    endDate: '',
    employeeIds: [],
    daysOfWeek: [],
  });

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const monthNames = [
    'Tháng 1',
    'Tháng 2',
    'Tháng 3',
    'Tháng 4',
    'Tháng 5',
    'Tháng 6',
    'Tháng 7',
    'Tháng 8',
    'Tháng 9',
    'Tháng 10',
    'Tháng 11',
    'Tháng 12',
  ];

  const goToPreviousWeek = () => {
    const newDate = moment(currentDate).subtract(1, 'week').toDate();
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = moment(currentDate).add(1, 'week').toDate();
    setCurrentDate(newDate);
  };

  const goToPreviousMonth = () => {
    const newDate = moment(currentDate).subtract(1, 'month').startOf('month').toDate();
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = moment(currentDate).add(1, 'month').startOf('month').toDate();
    setCurrentDate(newDate);
  };

  const getWeekNumber = (date) => {
    return moment(date).week();
  };

  const formatDate = (day) => {
    return moment(currentDate).date(day).format('YYYY-MM-DD');
  };

  const formatDateFromObj = (date) => {
    return moment(date).format('YYYY-MM-DD');
  };

  const getStartOfWeek = (date) => {
    return moment(date).startOf('isoWeek').toDate();
  };

  const getDateRange = useCallback(() => {
    if (viewMode === 'week') {
      const startOfWeek = moment(currentDate).startOf('isoWeek'); // Thứ 2
      const endOfWeek = moment(currentDate).endOf('isoWeek'); // Chủ nhật
      return {
        from: startOfWeek.format('YYYY-MM-DD'),
        to: endOfWeek.format('YYYY-MM-DD'),
      };
    } else {
      return {
        from: moment(currentDate).startOf('month').format('YYYY-MM-DD'),
        to: moment(currentDate).endOf('month').format('YYYY-MM-DD'),
      };
    }
  }, [currentDate, viewMode]);

  const handleDateClick = (day) => {
    const dateStr = formatDate(day);
    setSelectedDate(dateStr);
    setShowDetailModal(true);
  };

  const handleDateClickFromObj = (date) => {
    const dateStr = formatDateFromObj(date);
    setSelectedDate(dateStr);
    setShowDetailModal(true);
  };

  const getShiftsForDate = (dateStr) => {
    return shifts.filter((shift) => shift.date === dateStr && (!preferredFilter || shift.is_preferred));
  };

  // Check coverage cho tháng hiện tại
  const checkCoverage = useCallback(
    async (forceRefresh = false) => {
      if (!selectedBranch?.id) return;

      const month = moment(currentDate).format('YYYY-MM');
      const cacheKey = `${selectedBranch.id}-${month}`;

      // Kiểm tra cache
      if (!forceRefresh && coverageCache[cacheKey]) {
        setCoverageData(coverageCache[cacheKey]);
        return;
      }

      setCoverageLoading(true);
      try {
        const response = await shiftAPI.checkCoverage(selectedBranch.id, month);
        const datesWithShortage = response?.data?.dates_with_shortage || response?.dates_with_shortage || [];
        const coverageResult = {
          month,
          datesWithShortage: new Set(datesWithShortage),
        };
        setCoverageData(coverageResult);
        setCoverageCache((prev) => ({
          ...prev,
          [cacheKey]: coverageResult,
        }));
      } catch (error) {
        console.error('Error checking coverage:', error);
        toast.error('Lỗi khi kiểm tra coverage');
      } finally {
        setCoverageLoading(false);
      }
    },
    [selectedBranch?.id, currentDate, coverageCache],
  );

  // Load coverage khi thay đổi tháng hoặc branch
  useEffect(() => {
    if (selectedBranch?.id && viewMode === 'month') {
      checkCoverage();
    }
  }, [selectedBranch?.id, currentDate, viewMode, checkCoverage]);

  // Tính toán date range dựa trên period selection
  const getLowRegistrationDateRange = useCallback(() => {
    const now = moment();
    let start, end;

    switch (lowRegistrationPeriod) {
      case 'current-month':
        start = now.clone().startOf('month');
        end = now.clone().endOf('month');
        break;
      case 'next-month':
        start = now.clone().add(1, 'month').startOf('month');
        end = now.clone().add(1, 'month').endOf('month');
        break;
      case 'next-week':
        // Tuần tiếp theo: từ Thứ 2 tuần sau 7h sáng đến 7h sáng của ngày sau Chủ nhật
        start = now.clone().add(1, 'week').startOf('isoWeek'); // Thứ 2
        end = now.clone().add(1, 'week').endOf('isoWeek'); // Chủ nhật
        break;
      default:
        start = now.clone().startOf('month');
        end = now.clone().endOf('month');
    }

    // Áp dụng mốc thời gian 7h sáng đến 7h sáng hôm sau
    const dateFrom = start.clone().hour(0).minute(0).second(0).millisecond(0).toISOString();
    const dateTo = end.clone().add(1, 'day').hour(7).minute(0).second(0).millisecond(0).toISOString();

    return { dateFrom, dateTo };
  }, [lowRegistrationPeriod]);

  // Load các ca có ít đăng ký
  const loadLowRegistrationShifts = useCallback(
    async (page = 1) => {
      if (!selectedBranch?.id) return;

      setLowRegistrationLoading(true);
      try {
        const { dateFrom, dateTo } = getLowRegistrationDateRange();

        const response = await withBranch(shiftAPI.getShiftsGroupedByTemplate, {
          status: 'PENDING',
          page,
          limit: lowRegistrationLimit,
          date_from: dateFrom,
          date_to: dateTo,
        });

        const data = response?.data || [];
        const meta = response?.meta || {};
        const total = meta.total || data.length;

        setLowRegistrationShifts(data);
        setLowRegistrationTotal(total);
        setLowRegistrationPage(page);
      } catch (error) {
        console.error('Error loading low registration shifts:', error);
        toast.error('Lỗi khi tải danh sách ca có ít đăng ký');
      } finally {
        setLowRegistrationLoading(false);
      }
    },
    [selectedBranch?.id, withBranch, lowRegistrationLimit, getLowRegistrationDateRange],
  );

  // Load low registration shifts khi tab được chọn, branch thay đổi hoặc period thay đổi
  useEffect(() => {
    if (activeTab === 'low-registration' && selectedBranch?.id) {
      setLowRegistrationPage(1);
      loadLowRegistrationShifts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBranch?.id, lowRegistrationPeriod]);

  // Handler cho pagination
  const handleLowRegistrationPageChange = useCallback(
    (page) => {
      loadLowRegistrationShifts(page);
    },
    [loadLowRegistrationShifts],
  );

  // Load ca trống theo tuần được chọn
  const loadEmptySlots = useCallback(async () => {
    if (!selectedBranch?.id) return;

    setEmptySlotsLoading(true);
    try {
      const weekDate = moment(selectedWeek).format('YYYY-MM-DD');

      const response = await withBranch(shiftAPI.getEmptySlots, {
        week: weekDate,
      });

      const data = response?.data || response || [];
      setEmptySlots(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading empty slots:', error);
      toast.error('Lỗi khi tải danh sách ca trống');
      setEmptySlots([]);
    } finally {
      setEmptySlotsLoading(false);
    }
  }, [selectedBranch?.id, withBranch, selectedWeek]);

  // Load empty slots khi tab được chọn, branch thay đổi hoặc tuần thay đổi
  useEffect(() => {
    if (activeTab === 'empty-slots' && selectedBranch?.id) {
      loadEmptySlots();
    }
  }, [activeTab, selectedBranch?.id, selectedWeek, loadEmptySlots]);

  useEffect(() => {
    if (activeTab === 'bot-review' && selectedBranch?.id) {
      loadBotShifts();
      loadBotProposals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBranch?.id]);

  // #182918 — khung "Đã áp dụng…" chỉ đúng cho chi nhánh/tuần lúc áp dụng.
  useEffect(() => {
    setAppliedProposalResult(null);
    lastProposalTapRef.current = { id: null, at: 0 };
  }, [selectedBranch?.id, selectedWeek]);

  // #125027 — nạp lại số ca đã đăng ký theo nhân viên khi mở tab đề xuất, đổi chi nhánh hoặc
  // đổi tuần đang xem (số ca đăng ký tính theo đúng tuần đó).
  useEffect(() => {
    if (activeTab === 'bot-review' && selectedBranch?.id) {
      loadRegisteredCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBranch?.id, botWeekStart]);

  // #015110 — đổi tuần thì nạp lại đúng lô đề xuất của tuần đó (không đụng phần 2-3 phương án,
  // vốn chỉ sinh cho tuần tới).
  useEffect(() => {
    if (activeTab === 'bot-review' && selectedBranch?.id) {
      loadBotShifts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botWeekStart]);

  // Handler chuyển tuần cho tab ca trống
  const goToPreviousWeekForEmptySlots = () => {
    setSelectedWeek(moment(selectedWeek).subtract(1, 'week').startOf('isoWeek'));
  };

  const goToNextWeekForEmptySlots = () => {
    setSelectedWeek(moment(selectedWeek).add(1, 'week').startOf('isoWeek'));
  };

  const goToCurrentWeekForEmptySlots = () => {
    setSelectedWeek(moment().startOf('isoWeek'));
  };

  const goToNextWeekFromNowForEmptySlots = () => {
    setSelectedWeek(moment().add(1, 'week').startOf('isoWeek'));
  };

  // Tính toán thông tin tuần để hiển thị
  const weekInfo = useMemo(() => {
    const weekStart = moment(selectedWeek).startOf('isoWeek'); // Thứ 2
    const weekEnd = moment(weekStart).add(6, 'days'); // Chủ nhật
    const weekNumber = moment(weekStart).week();
    const month = weekStart.format('MM');
    const year = weekStart.format('YYYY');

    return {
      weekStart,
      weekEnd,
      weekNumber,
      month,
      year,
      displayText: `Tuần ${weekNumber} (${weekStart.format('DD/MM')} - ${weekEnd.format('DD/MM/YYYY')})`,
    };
  }, [selectedWeek]);

  // Handler set boost
  const handleSetBoost = async () => {
    if (!selectedSlotForBoost || !selectedBranch?.id) return;

    const user = getUserFromLocalStorage();
    if (!user?.id) {
      toast.error('Không tìm thấy thông tin người dùng');
      return;
    }

    try {
      setLoading(true);
      await withBranch(shiftAPI.setSlotBoost, {
        template_id: selectedSlotForBoost.template_id,
        date: selectedSlotForBoost.date,
        reward_rate_bonus: boostRewardRate,
        current_user_id: user.id,
      });

      toast.success('Đã set boost thưởng thành công');
      setShowBoostModal(false);
      setSelectedSlotForBoost(null);
      setBoostRewardRate(0);
      await loadEmptySlots();
    } catch (error) {
      toast.error(error?.message || 'Set boost thất bại');
    } finally {
      setLoading(false);
    }
  };

  // Handler xóa boost
  const handleRemoveBoost = async (slot) => {
    if (!selectedBranch?.id) return;

    if (
      !window.confirm(
        `Bạn có chắc chắn muốn xóa boost cho ca "${slot.template_name}" ngày ${moment(slot.date).format(
          'DD/MM/YYYY',
        )}?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await withBranch(shiftAPI.removeSlotBoost, {
        template_id: slot.template_id,
        date: slot.date,
      });

      toast.success('Đã xóa boost thành công');
      await loadEmptySlots();
    } catch (error) {
      toast.error(error?.message || 'Xóa boost thất bại');
    } finally {
      setLoading(false);
    }
  };

  // ─── Status config (dùng chung toàn trang) ────────────────────────────────
  const STATUS_CONFIG = {
    PENDING: { label: 'Chờ duyệt', color: 'warning' },
    // #190609 — bot KHÔNG còn tự duyệt (lệnh sếp 19:42 04/09/2026). PROPOSED_BOT là ĐỀ XUẤT
    // chưa duyệt; APPROVE_BOT chỉ còn là dữ liệu CŨ của cơ chế đã bỏ.
    // #015110 — nói thẳng "chưa duyệt" ngay trên nhãn: trên bảng lịch tuần chỉ có mỗi nhãn này
    // phân biệt ca đề xuất với ca đã duyệt thật.
    PROPOSED_BOT: { label: 'Đề xuất — chưa duyệt', color: 'warning' },
    APPROVED: { label: 'Đã duyệt', color: 'success' },
    APPROVE_BOT: { label: 'Đã duyệt (bot cũ)', color: 'success' },
    IN_PROGRESS: { label: 'Đang làm', color: 'primary' },
    COMPLETED: { label: 'Hoàn thành', color: 'secondary' },
    COMPLETED_WAIT_APPROVE: { label: 'Chờ xác nhận', color: 'warning' },
    REJECTED: { label: 'Từ chối', color: 'danger' },
    CANCELLED: { label: 'Đã hủy', color: 'secondary' },
    ABSENT: { label: 'Vắng mặt', color: 'danger' },
  };
  const getStatusConfig = (status) => STATUS_CONFIG[status] || { label: status, color: 'secondary' };

  // ─── Auto Approve ──────────────────────────────────────────────────────────
  const handleAutoApprove = async () => {
    if (!window.confirm('Chạy Auto Approve cho tuần tới? Bot sẽ tự động duyệt các ca PENDING có template.')) return;
    try {
      setAutoApproveLoading(true);
      const weekStart = moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      const res = await shiftAPI.autoApprove(weekStart, selectedBranch?.id);
      const d = res?.data ?? res;
      toast.success(
        `🤖 Auto Approve xong: ${d.approved} ca duyệt, ${d.skipped} ca giữ PENDING, ${d.slots_processed} slot`,
      );
      setAppliedProposalResult(null); // #191002
      loadBotShifts();
    } catch (err) {
      toast.error('Auto Approve thất bại: ' + (err?.message || 'Lỗi không xác định'));
    } finally {
      setAutoApproveLoading(false);
    }
  };

  // ─── #015110: bảng ca đề xuất dạng LỊCH TUẦN ───────────────────────────────

  const botWeekInfo = useMemo(() => {
    const start = moment(botWeekStart).startOf('isoWeek');
    const end = moment(start).add(6, 'days');
    return {
      start,
      end,
      label: `Tuần ${start.week()} (${start.format('DD/MM')} - ${end.format('DD/MM/YYYY')})`,
    };
  }, [botWeekStart]);

  // #001848 — nạp MỘT lần danh sách đăng ký ca ưu tiên (đã duyệt) của chi nhánh đang chọn.
  useEffect(() => {
    if (!selectedBranch?.id) {
      setBranchPreferences([]);
      return undefined;
    }
    let cancelled = false;
    shiftPreferenceAPI
      .getShiftPreferences({ branch_id: selectedBranch.id, status: 'APPROVED', limit: 1000 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        setBranchPreferences(list.filter((p) => p.is_active !== false));
      })
      .catch(() => {
        if (!cancelled) setBranchPreferences([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBranch?.id]);

  /**
   * #001848 — tra "ai cùng đăng ký ca này". Khoá = mẫu ca + thứ trong tuần (0=CN..6=T7), đúng
   * điều kiện backend dùng ở `GET /shifts/available-employees`
   * (`shift_template_id` + `days_of_week` chứa thứ đó), để hai chỗ không nói hai danh sách khác nhau.
   */
  const registrantsByTemplateDay = useMemo(() => {
    const map = new Map();
    branchPreferences.forEach((p) => {
      const templateId = p.shift_template_id || p.shift_template?.id;
      const employeeId = p.employee_id || p.employee?.id;
      if (!templateId || !employeeId) return;
      (p.days_of_week || []).forEach((dow) => {
        const key = `${templateId}|${dow}`;
        if (!map.has(key)) map.set(key, []);
        const bucket = map.get(key);
        if (bucket.some((e) => e.id === employeeId)) return;
        bucket.push({
          id: employeeId,
          full_name: p.employee?.full_name || p.employee?.username || '',
        });
      });
    });
    return map;
  }, [branchPreferences]);

  /** #001848 — ai đã được đề xuất BAO NHIÊU ca trong tuần đang xem (tra theo id nhân viên). */
  const proposedCountByEmployee = useMemo(() => {
    const counts = {};
    botShifts.forEach((s) => {
      if (!s.employee_id) return;
      counts[s.employee_id] = (counts[s.employee_id] || 0) + 1;
    });
    return counts;
  }, [botShifts]);

  /** #001848 — cùng số liệu trên nhưng kèm tên, xếp người bị dồn nhiều ca nhất lên trước. */
  const proposedCountRows = useMemo(() => {
    const byId = new Map();
    botShifts.forEach((s) => {
      if (!s.employee_id) return;
      const row = byId.get(s.employee_id) || {
        id: s.employee_id,
        name: s.employee_name || 'Chưa xác định',
        count: 0,
      };
      row.count += 1;
      byId.set(s.employee_id, row);
    });
    return Array.from(byId.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [botShifts]);

  /**
   * Ca đề xuất của MỘT ngày, đã đưa về đúng hình dạng mà khung lịch tuần dùng chung cần.
   * Giữ luôn bản ghi gốc ở `raw` để modal sửa tay có đủ start_time/end_time/template_id thật.
   */
  const getProposedShiftsForDate = useCallback(
    (dateStr) =>
      botShifts
        .filter((s) => moment(s.start_time).format('YYYY-MM-DD') === dateStr)
        .sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf())
        .map((s) => ({
          id: s.id,
          status: s.status,
          employee_id: s.employee_id,
          employee_name: s.employee_name || 'Chưa xác định',
          template_id: s.template_id,
          template_name: s.template_name || s.shift_type || 'Ca',
          time: `Ca ${moment(s.start_time).format('HH:mm')}`,
          is_preferred: s.is_preferred || false,
          // #001848 — người CÙNG đăng ký ca này, trừ chính người đang được đề xuất.
          registrants: (registrantsByTemplateDay.get(`${s.template_id}|${moment(s.start_time).day()}`) || []).filter(
            (e) => e.id !== s.employee_id,
          ),
          raw: s,
        })),
    [botShifts, registrantsByTemplateDay],
  );

  /**
   * #001848 — đổi NGƯỜI của một ca đề xuất chỉ bằng cách bấm vào tên người cùng đăng ký hiện ngay
   * dưới ca đó ("để có thể đổi đề xuất một cách nhanh chóng"). Vẫn hỏi xác nhận: chữ ở dòng đó rất
   * nhỏ nên bấm nhầm là chuyện có thật, mà đổi người là đổi lịch làm của người khác.
   */
  const handleQuickSwapProposedEmployee = async (shift, employee) => {
    const when = moment(shift.raw?.start_time).format('ddd DD/MM HH:mm');
    if (!window.confirm(`Đổi đề xuất ca ${when} từ ${shift.employee_name} sang ${employee.full_name}?`)) return;

    try {
      setQuickSwapShiftId(shift.id);
      await shiftAPI.updateShift(shift.id, { employee_id: employee.id });
      setAppliedProposalResult(null); // #191002 — sửa lẻ sau áp dụng ⇒ số trong khung hết đúng
      toast.success(`Đã đổi ca ${when} sang ${employee.full_name}`);
      await loadBotShifts();
    } catch (err) {
      // Backend từ chối vì trùng giờ / khác chi nhánh / mẫu ca không mở ngày đó — nói nguyên câu.
      toast.error(getErrorMessage(err) || 'Đổi người đề xuất thất bại');
    } finally {
      setQuickSwapShiftId(null);
    }
  };

  const handleBulkApproveProposedWeek = async () => {
    if (!botShifts.length) {
      toast.info('Tuần này chưa có đề xuất nào để duyệt');
      return;
    }
    // #180331 + #182435 + lệnh sếp `ca_bo_qua`: ca không còn hợp lệ (mẫu ca đã tắt / đổi ngày /
    // đổi giờ / đổi thời lượng / đổi chi nhánh / mẫu ca bị xoá) không chặn lô nữa — vẫn giữ
    // nguyên (không xoá), nói rõ trong câu xác nhận.
    const totalCount = botShifts.length + staleBotShifts.length;
    const confirmMsg =
      staleBotShifts.length > 0
        ? `Duyệt ${botShifts.length} ca đề xuất hợp lệ của ${botWeekInfo.label}?\n\n` +
          `Bỏ qua ${staleBotShifts.length}/${totalCount} ca không còn khớp cấu hình ca hiện tại — vẫn giữ nguyên, không xoá.`
        : `Duyệt tất cả ${botShifts.length} ca đề xuất của ${botWeekInfo.label}?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setBulkApproving(true);
      const res = await shiftAPI.bulkApproveShifts(
        botShifts.map((s) => s.id),
        getUserFromLocalStorage()?.id,
      );
      const approved = res?.approved ?? res?.data?.approved ?? 0;
      const total = res?.total ?? res?.data?.total ?? botShifts.length;
      const skipped = res?.skipped ?? res?.data?.skipped ?? [];
      // #191002 — ca vừa áp dụng nay đã duyệt, không còn "Đề xuất — chưa duyệt" ⇒ bỏ khung.
      setAppliedProposalResult(null);
      // #180331 — con số tổng kết phải nói THẬT: "đã duyệt X/Y", kèm BÁO RIÊNG từng ca không
      // duyệt được (ai / ngày giờ / lý do). Ca không duyệt được KHÔNG bị tự dọn.
      if (skipped.length > 0) {
        toast.success(
          `Đã duyệt ${approved}/${total} ca đề xuất của ${botWeekInfo.label} — ${skipped.length} ca không duyệt được`,
          { autoClose: 10000 },
        );
        skipped.slice(0, 5).forEach((f) => {
          const who = f.employee_name || f.employee_id || f.shift_id;
          const when = f.start_time ? moment(f.start_time).format('ddd DD/MM HH:mm') : '';
          toast.warn(`${who}${when ? ` — ${when}` : ''}: ${f.reason}`, { autoClose: 15000 });
        });
        if (skipped.length > 5) {
          toast.warn(`... và ${skipped.length - 5} ca không duyệt được khác`, { autoClose: 10000 });
        }
      } else {
        toast.success(`Đã duyệt ${approved}/${total} ca đề xuất của ${botWeekInfo.label}`);
      }
      await loadBotShifts();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Duyệt hàng loạt thất bại', { autoClose: 10000 });
    } finally {
      setBulkApproving(false);
    }
  };

  const handleRevokeAllBotShifts = async () => {
    if (!botShifts.length) {
      toast.info('Không có ca Bot duyệt để revoke');
      return;
    }

    if (!window.confirm(`Bỏ tất cả ${botShifts.length} đề xuất của bot, trả ca về PENDING?`)) return;

    try {
      setLoading(true);
      const weekStart = moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      const res = await withBranch(shiftAPI.revokeAllAutoApproved, { weekStart });
      const updated = res?.updated ?? res?.data?.updated ?? 0;
      // #191002 — ca đã áp dụng vừa bị trả hết về PENDING ⇒ khung "Đã áp dụng…" không còn đúng.
      setAppliedProposalResult(null);
      toast.success(`Đã revoke ${updated} ca về PENDING`);
      await Promise.all([loadBotShifts(), loadBotProposals()]);
    } catch (err) {
      toast.error(err?.message || 'Revoke all Bot duyệt thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAllApprovedShifts = async () => {
    if (
      !window.confirm(
        'Revoke TOÀN BỘ ca đã duyệt của tuần tới về PENDING — bao gồm cả ca Bot duyệt LẪN ca duyệt tay (APPROVED)?\n\nHành động này KHÔNG chỉ giới hạn ở Bot, dùng khi cần reset sạch để test lại.',
      )
    )
      return;

    try {
      setLoading(true);
      const weekStart = moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      const res = await withBranch(shiftAPI.revokeAllApprovedIncludingManual, { weekStart });
      const updated = res?.updated ?? res?.data?.updated ?? 0;
      // #191002 — như handleRevokeAllBotShifts: xoá khung "Đã áp dụng…" đã lỗi thời.
      setAppliedProposalResult(null);
      toast.success(`Đã revoke ${updated} ca (Bot + duyệt tay) về PENDING`);
      await Promise.all([loadBotShifts(), loadBotProposals()]);
    } catch (err) {
      toast.error(err?.message || 'Revoke all thất bại');
    } finally {
      setLoading(false);
    }
  };

  const loadBotShifts = async () => {
    try {
      setBotShiftsLoading(true);
      // #015110 — theo TUẦN ĐANG XEM của bảng đề xuất, không còn khoá cứng "tuần tới".
      const weekStart = moment(botWeekStart).startOf('isoWeek').format('YYYY-MM-DD');
      const weekEnd = moment(botWeekStart).endOf('isoWeek').format('YYYY-MM-DD');
      const res = await withBranch(shiftAPI.getShifts, {
        // #190609 — tab này nay là "Đề xuất của bot": bot chỉ sinh PROPOSED_BOT, không tự duyệt.
        status: 'PROPOSED_BOT',
        date_from: weekStart,
        date_to: weekEnd,
        limit: 500,
      });
      // #180331 — tách đề xuất KHÔNG còn hợp lệ (mẫu ca đã tắt/đổi ngày) ra khỏi danh sách duyệt:
      // hiện NGAY khi mở màn (không cần chạy lại bot), không lọt vào "Duyệt tất cả", và vẫn được
      // BÁO RIÊNG (banner) chứ không im lặng biến mất.
      const all = res?.data || [];
      setBotShifts(all.filter((s) => !s.is_stale_proposal));
      setStaleBotShifts(all.filter((s) => s.is_stale_proposal));
    } catch {
      setBotShifts([]);
      setStaleBotShifts([]);
    } finally {
      setBotShiftsLoading(false);
    }
  };

  // #125027 — đếm số ca MỖI NHÂN VIÊN ĐÃ ĐĂNG KÝ trong tuần đang xem của đề xuất bot.
  // Dùng chung bộ trạng thái "đã đăng ký" của máy chủ (REGISTERED_SHIFT_STATUSES) và gom theo
  // employee_id một lần cho cả chi nhánh (không gọi lại cho từng nhân viên — tránh N+1).
  const loadRegisteredCounts = async () => {
    if (!selectedBranch?.id) {
      setRegisteredCountsByEmployee({});
      return;
    }
    try {
      const weekStart = moment(botWeekStart).startOf('isoWeek').format('YYYY-MM-DD');
      const weekEnd = moment(botWeekStart).endOf('isoWeek').format('YYYY-MM-DD');
      const res = await withBranch(shiftAPI.getShifts, {
        date_from: weekStart,
        date_to: weekEnd,
        limit: 1000,
      });
      const counts = {};
      (res?.data || []).forEach((s) => {
        if (!s.employee_id || !REGISTERED_SHIFT_STATUSES.includes(s.status)) return;
        counts[s.employee_id] = (counts[s.employee_id] || 0) + 1;
      });
      setRegisteredCountsByEmployee(counts);
    } catch {
      setRegisteredCountsByEmployee({});
    }
  };

  const loadBotProposals = async () => {
    try {
      setProposalLoading(true);
      const weekStart = moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      const res = await shiftAPI.generateAutoApproveProposals({
        weekStart,
        branchId: selectedBranch?.id,
        // #133908 — nới trần 3 → 5; máy chủ vẫn chỉ trả về số phương án KHÁC BIỆT THẬT SỰ.
        options: 5,
      });
      const options = res?.options || res?.data?.options || [];
      const meta = res?.meta || res?.data?.meta;
      setBotProposals(options);
      setProposalRevenueMeta(meta?.revenuePriority || null);
      setProposalShortageReason(meta?.optionShortageReason || null);
      if (options.length > 0) {
        setSelectedProposalId(options[0].id);
      } else {
        setSelectedProposalId('');
        setFloorSuggestionData(null);
      }
      setProposalItemsOverride({});
      return options;
    } catch (err) {
      setBotProposals([]);
      setProposalRevenueMeta(null);
      setProposalShortageReason(null);
      setSelectedProposalId('');
      setFloorSuggestionData(null);
      toast.error(err?.message || 'Không tải được phương án bot');
      return null;
    } finally {
      setProposalLoading(false);
    }
  };

  const loadFloorSuggestions = async (proposalIdParam = selectedProposalId) => {
    if (!selectedBranch?.id) return;
    if (!proposalIdParam) {
      setFloorSuggestionData(null);
      return;
    }
    try {
      setFloorSuggestionLoading(true);
      const weekStart = moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      const res = await shiftAPI.getAutoApproveFloorSuggestions({
        weekStart,
        branchId: selectedBranch?.id,
        proposalId: proposalIdParam,
        minShifts: 4,
      });
      setFloorSuggestionData(res?.data || res || null);
    } catch (error) {
      console.error('Error loading floor suggestions:', error);
      setFloorSuggestionData(null);
      toast.error(error?.message || 'Không tải được gợi ý mốc tối thiểu 4 ca');
    } finally {
      setFloorSuggestionLoading(false);
    }
  };

  const proposalOverrideKey = (item) => item?.shift_id || item?.slot_key;

  // --- #111404: hiển thị lý do bot duyệt/không duyệt theo doanh thu ---
  const formatVnd = (value) => `${Math.round(Number(value) || 0).toLocaleString('vi-VN')}đ`;

  // #133908 — tên tiếng Việt của 4 yếu tố mềm, dùng khi hiện mức đáp ứng của phương án.
  const BOT_FACTOR_SHORT_LABEL = {
    balance_workload: 'cân bằng tải',
    // #201334 — yếu tố "ca đang chờ" đã được thay bằng "tỉ lệ được duyệt".
    approval_rate: 'tỉ lệ được duyệt',
    pending_boost: 'ca đang chờ',
    preference: 'nguyện vọng',
    new_employee: 'nhân viên mới',
  };

  const SHIFT_TIER_LABEL = {
    HIGH: { text: 'Ca ĐÔNG', bg: 'danger' },
    MEDIUM: { text: 'Ca trung bình', bg: 'warning' },
    LOW: { text: 'Ca vắng', bg: 'secondary' },
    UNKNOWN: { text: 'Chưa đủ dữ liệu', bg: 'light' },
  };

  const renderShiftTierBadge = (item) => {
    const tier = SHIFT_TIER_LABEL[item?.shift_revenue_tier];
    if (!tier) return null;
    return (
      <Badge
        bg={tier.bg}
        text={tier.bg === 'light' || tier.bg === 'warning' ? 'dark' : undefined}
      >
        {tier.text}
        {item.shift_revenue_rank ? ` #${item.shift_revenue_rank}/${item.shift_revenue_total_ranked}` : ''} —{' '}
        {formatVnd(item.shift_avg_revenue)}/ca
      </Badge>
    );
  };


  const selectedProposal = useMemo(
    () => botProposals.find((p) => p.id === selectedProposalId) || botProposals[0] || null,
    [botProposals, selectedProposalId],
  );

  // Nhu cầu nhân sự của lịch tuần tới hiện chưa có người (trước khi bot duyệt).
  // Fallback theo thứ tự ưu tiên để vẫn chạy được với bản server cũ chưa có trường mới.
  const calendarDemandUncovered =
    selectedProposal?.stats?.calendarDemandUncovered ??
    selectedProposal?.stats?.calendarEmptySlotsByCapacity ??
    selectedProposal?.stats?.calendarEmptySlotRows ??
    0;

  // Số ca CÒN THIẾU người sau khi áp phương án bot đang chọn.
  // Server mới đã trả sẵn ở stats.calendarEmptySlots; nếu server cũ (giá trị bằng đúng
  // emptySlots của mô phỏng) thì tự tính lại từ nhu cầu lịch - số ca bot duyệt được.
  const stillMissingShifts = useMemo(() => {
    if (!selectedProposal) return 0;
    const stats = selectedProposal.stats || {};
    const filled = stats.filledSlots ?? 0;
    if (stats.calendarStatsAvailable) return stats.calendarEmptySlots ?? 0;
    return Math.max(0, calendarDemandUncovered - filled);
  }, [selectedProposal, calendarDemandUncovered]);

  // #180848 — từng ca CÒN THIẾU người của phương án đang chọn, gom theo NGÀY để Lịch tuần nói rõ
  // "ngày nào, ca nào còn thiếu mấy người" thay vì chỉ 1 con số tổng `stillMissingShifts`.
  // Server cũ chưa trả `calendarEmptySlotBreakdown` -> map rỗng -> Lịch tuần giữ nguyên như trước,
  // badge tổng vẫn chạy bình thường (không vỡ màn hình).
  const missingSlotsByDate = useMemo(() => {
    const rows = selectedProposal?.stats?.calendarEmptySlotBreakdown || [];
    const map = {};
    for (const row of rows) {
      if (!row?.date || !(row.still_missing > 0)) continue;
      if (!map[row.date]) map[row.date] = [];
      map[row.date].push(row);
    }
    for (const date of Object.keys(map)) {
      map[date].sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());
    }
    return map;
  }, [selectedProposal]);

  // #225256 (góp ý Trợ năng: "dưới phần thông tin ca đề xuất, thì hiển thị ca thiếu người,
  // gọn gàng tiết kiệm diện tích nhưng vẫn đủ thông tin ca") — danh sách phẳng từng CA còn
  // thiếu người của phương án đang chọn, xếp theo thời gian. Cùng nguồn với `missingSlotsByDate`
  // (Lịch tuần đã hiện theo từng ngày); ở kiểu xem Danh sách trước đây chỉ có 1 dòng chữ tổng nên
  // hiện thêm dạng chip gọn để thấy RÕ ca nào còn thiếu. Server cũ chưa trả breakdown → rỗng.
  const missingShiftRows = useMemo(() => {
    const rows = selectedProposal?.stats?.calendarEmptySlotBreakdown || [];
    return rows
      .filter((row) => row?.start_time && row.still_missing > 0)
      .slice()
      .sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());
  }, [selectedProposal]);

  // Danh sách đăng ký bot CỐ TÌNH không tự duyệt (NV mới xin ca đông) — quản lý quyết bằng tay.
  const manualReviewItems = useMemo(() => selectedProposal?.manual_review_items || [], [selectedProposal]);
  // #190609 — NV chưa đủ ca tập việc: BOT không đề xuất ca chính (ca thu ngân). Đây là rào
  // cứng duy nhất còn lại; duyệt tay và NV tự đăng ký đều không bị chặn.
  // Khác manual_review_items: bot không đưa các ca này vào phương án, nhưng đăng ký của NV vẫn
  // nằm ở mục chờ duyệt và quản lý DUYỆT TAY được bình thường (#190609 lượt 4).
  const newHireCashierExcluded = useMemo(
    () => selectedProposal?.new_hire_cashier_excluded || [],
    [selectedProposal]
  );

  const selectedProposalItems = useMemo(() => {
    if (!selectedProposal) return [];

    const mapped = selectedProposal.items.map((item) => {
      const key = proposalOverrideKey(item);
      const overrideEmployeeId =
        proposalItemsOverride[key] ?? proposalItemsOverride[item.slot_key];
      if (!overrideEmployeeId) return item;
      const candidate = item.candidates?.find((c) => c.employee_id === overrideEmployeeId);
      if (!candidate) return item;
      return {
        ...item,
        shift_id: candidate.shift_id,
        employee_id: candidate.employee_id,
        employee_name: candidate.employee_name,
        is_preferred: candidate.is_preferred,
      };
    });

    // Sắp xếp theo thời gian tăng dần để dễ review: Thứ 2 08:00 -> ... -> Chủ nhật
    return mapped.sort((a, b) => {
      const aStart = moment(a.start_time).valueOf();
      const bStart = moment(b.start_time).valueOf();
      if (aStart !== bStart) return aStart - bStart;

      const aEnd = moment(a.end_time).valueOf();
      const bEnd = moment(b.end_time).valueOf();
      if (aEnd !== bEnd) return aEnd - bEnd;

      return (a.template_name || '').localeCompare(b.template_name || '');
    });
  }, [selectedProposal, proposalItemsOverride]);

  // #125027 — mỗi nhân viên trong đề xuất đang chọn: bot đề xuất bao nhiêu ca + đã đăng ký bao
  // nhiêu ca trong tuần đang xem. Gộp 2 nguồn sẵn có (thống kê phương án + số ca đăng ký theo NV).
  const proposedEmployeeRows = useMemo(() => {
    const rows = selectedProposal?.stats?.shiftsPerEmployee || [];
    return rows.map((row) => ({
      id: row.employee_id,
      name: row.employee_name || row.employee_id || 'Chưa xác định',
      proposed: row.count ?? 0,
      registered: registeredCountsByEmployee[row.employee_id] ?? 0,
    }));
  }, [selectedProposal, registeredCountsByEmployee]);

  useEffect(() => {
    if (activeTab !== 'bot-review' || !selectedBranch?.id || !selectedProposalId) {
      return;
    }
    loadFloorSuggestions(selectedProposalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposalId, activeTab, selectedBranch?.id]);

  // #182918 — số ca thiếu người (Y) và trống trong slot có đơn (Z) của một đề xuất khi CHƯA áp
  // chính nó. Máy chủ đã tính ca "Đề xuất — chưa duyệt" (PROPOSED_BOT) là đã giữ chỗ, nên với
  // danh sách đề xuất nạp lại sau áp dụng: phần đề xuất mới lấp (filledSlots) cộng phần nó vẫn để
  // trống = đúng phần còn lại sau lần áp dụng vừa rồi.
  const remainingBeforeOption = (option) => {
    const stats = option?.stats || {};
    const filled = stats.filledSlots ?? 0;
    const missingAfter = stats.calendarStatsAvailable
      ? stats.calendarEmptySlots ?? 0
      : Math.max(0, (stats.calendarDemandUncovered ?? 0) - filled);
    return {
      missing: missingAfter + filled,
      emptyInRegistered: (stats.emptySlots ?? 0) + filled,
    };
  };

  // #212630 → #182918 — CHẠM ĐÚP vào một đề xuất mới là áp dụng (chạm đúp = đã xác nhận, không
  // hỏi lại). Chạm 1 lần chỉ để xem số — xem handleProposalTap.
  const applyProposalOption = async (option) => {
    const items = option?.items || [];
    if (!items.length) {
      toast.info('Đề xuất này không có ca nào để áp dụng');
      return;
    }

    try {
      setApplyProposalLoading(true);
      setSelectedProposalId(option.id);
      setAppliedProposalResult(null);
      const shiftIds = items.map((i) => i.shift_id);
      const res = await shiftAPI.applyAutoApproveProposal(shiftIds);
      const updated = res?.updated ?? res?.data?.updated ?? 0;
      const appliedAt = moment().format('HH:mm');
      const [, nextOptions] = await Promise.all([loadBotShifts(), loadBotProposals()]);
      // Sau áp dụng không còn đề xuất nào ⇒ lấy số dự tính của chính đề xuất vừa áp dụng.
      const remaining =
        nextOptions && nextOptions.length > 0
          ? remainingBeforeOption(nextOptions[0])
          : {
              missing: option.stats?.calendarStatsAvailable
                ? option.stats?.calendarEmptySlots ?? 0
                : Math.max(
                    0,
                    (option.stats?.calendarDemandUncovered ?? 0) - (option.stats?.filledSlots ?? 0),
                  ),
              emptyInRegistered: option.stats?.emptySlots ?? 0,
            };
      setAppliedProposalResult({
        name: option.name,
        at: appliedAt,
        updated,
        missing: remaining.missing,
        emptyInRegistered: remaining.emptyInRegistered,
      });
      toast.success(`Đã áp dụng ${option.name}: ${updated} ca chuyển sang Đề xuất — chưa duyệt`);
    } catch (err) {
      toast.error(err?.message || 'Áp dụng đề xuất thất bại');
    } finally {
      setApplyProposalLoading(false);
    }
  };

  // #182918 — chạm 1 lần = chỉ chọn để XEM số của đề xuất; chạm lần 2 trên CÙNG nút trong 0,4 giây
  // = áp dụng. Tự so mốc giờ giữa 2 lần chạm (nhấp đúp chuột cũng ra 2 lần click nên dùng chung).
  const PROPOSAL_DOUBLE_TAP_MS = 400;
  const handleProposalTap = (option) => {
    if (applyProposalLoading) return;
    const now = Date.now();
    const last = lastProposalTapRef.current;
    if (last.id === option.id && now - last.at <= PROPOSAL_DOUBLE_TAP_MS) {
      lastProposalTapRef.current = { id: null, at: 0 };
      applyProposalOption(option);
      return;
    }
    lastProposalTapRef.current = { id: option.id, at: now };
    setAppliedProposalResult(null);
    setSelectedProposalId(option.id);
  };

  // Mở modal set boost
  const openBoostModal = (slot) => {
    setSelectedSlotForBoost(slot);
    setBoostRewardRate(slot.has_boost ? slot.boost_reward_rate_bonus : 0);
    setShowBoostModal(true);
  };

  // #195455 — 3 hàm thao tác lẻ (duyệt / từ chối / huỷ duyệt) TRẢ VỀ true khi máy chủ đã nhận thao
  // tác (CSDL đã đổi), false khi thất bại — lỗi vẫn tự bắt + toast bên trong như cũ. Nơi gọi cần
  // biết kết quả (vd xoá khung "Đã áp dụng…" của khối đề xuất bot) thì đọc giá trị trả về; đừng
  // làm việc "sau khi thành công" vô điều kiện sau `await handleX(...)`.
  const handleApproveShift = async (shiftId) => {
    let done = false;
    try {
      setLoading(true);
      // #190609 — PHẢI truyền id người đang bấm: backend lấy `current_user_id` từ body để ghi
      // `approved_by` và AuditLog "ai duyệt". Thiếu tham số này thì ca duyệt xong vẫn không
      // biết ai duyệt (đo thật 04/09/2026: approved_by = NULL).
      await shiftAPI.approveShift(shiftId, getUserFromLocalStorage()?.id);
      done = true;

      // Reload data after approval
      const { from, to } = getDateRange();

      const shiftsResponse = await withBranch(shiftAPI.getShifts, {
        date_from: from,
        date_to: to,
        page: 1,
        limit: 1000,
        status: statusFilter || '',
      });

      const mapped = (shiftsResponse.data || [])
        .map((s) => {
          const date = moment(s.start_time).format('YYYY-MM-DD');
          const time = `Ca ${moment(s.start_time).format('HH:mm')}`;
          const start_ts = moment(s.start_time).valueOf();

          return {
            id: s.id,
            date,
            type: s.shift_type || 'NORMAL',
            time,
            status: s.status,
            employee_id: s.employee_id,
            employee_name: s.employee_name || 'Chưa xác định',
            template_id: s.template_id,
            template_name: s.template_name || s.shift_type,
            start_ts,
            is_preferred: s.is_preferred || false,
            approved_shifts: s.approved_shifts || 0,
            pending_shifts: s.pending_shifts || 0,
            rejected_shifts: s.rejected_shifts || 0,
          };
        })
        .sort((a, b) => {
          // Sắp xếp: ca ưu tiên lên đầu, sau đó theo thời gian
          if (a.is_preferred !== b.is_preferred) {
            return b.is_preferred ? 1 : -1;
          }
          return a.start_ts - b.start_ts;
        });

      setShifts(mapped);
      const pending = mapped.filter((s) => s.status === 'PENDING');
      setPendingShifts(pending);

      // Reload low registration shifts nếu đang ở tab đó
      if (activeTab === 'low-registration') {
        await loadLowRegistrationShifts(lowRegistrationPage);
      }

      toast.success('Đã duyệt ca thành công');
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Duyệt ca thất bại');
    } finally {
      setLoading(false);
    }
    return done;
  };

  const handleRejectShift = async (shiftId) => {
    let done = false;
    // #201334 — hỏi lý do TRƯỚC khi gọi máy chủ; bấm Hủy thì không từ chối gì cả.
    const target =
      shifts.find((s) => s.id === shiftId) || botShifts.find((s) => s.id === shiftId);
    const targetLabel = target
      ? [
          target.employee_name || target.employee?.full_name,
          target.start_time || target.start_ts
            ? moment(target.start_time || target.start_ts).format('HH:mm DD/MM/YYYY')
            : target.date,
        ]
          .filter(Boolean)
          .join(' — ')
      : '';
    // #222214 — hộp lý do tự gọi máy chủ và GIỮ MỞ tới khi từ chối xong; lỗi (vd mất mạng) thì
    // báo ngay trong hộp, lý do vừa gõ không mất. Tới đây nghĩa là ca ĐÃ bị từ chối.
    // #195455 — bấm Hủy / không từ chối được thì trả false để nơi gọi KHÔNG xoá khung "Đã áp dụng…".
    const reason = await askRejectReason(targetLabel, (r) =>
      shiftAPI.rejectShift(shiftId, undefined, r)
    );
    if (!reason) return done;
    done = true;
    try {
      setLoading(true);

      // Reload data after rejection
      const { from, to } = getDateRange();

      const shiftsResponse = await withBranch(shiftAPI.getShifts, {
        date_from: from,
        date_to: to,
        page: 1,
        limit: 1000,
        status: statusFilter || '',
      });

      const mapped = (shiftsResponse.data || [])
        .map((s) => {
          const date = moment(s.start_time).format('YYYY-MM-DD');
          const time = `Ca ${moment(s.start_time).format('HH:mm')}`;
          const start_ts = moment(s.start_time).valueOf();

          return {
            id: s.id,
            date,
            type: s.shift_type || 'NORMAL',
            time,
            status: s.status,
            employee_id: s.employee_id,
            employee_name: s.employee_name || 'Chưa xác định',
            template_id: s.template_id,
            template_name: s.template_name || s.shift_type,
            start_ts,
            is_preferred: s.is_preferred || false,
            approved_shifts: s.approved_shifts || 0,
            pending_shifts: s.pending_shifts || 0,
            rejected_shifts: s.rejected_shifts || 0,
          };
        })
        .sort((a, b) => {
          // Sắp xếp: ca ưu tiên lên đầu, sau đó theo thời gian
          if (a.is_preferred !== b.is_preferred) {
            return b.is_preferred ? 1 : -1;
          }
          return a.start_ts - b.start_ts;
        });

      setShifts(mapped);
      const pending = mapped.filter((s) => s.status === 'PENDING');
      setPendingShifts(pending);

      // Reload low registration shifts nếu đang ở tab đó
      if (activeTab === 'low-registration') {
        await loadLowRegistrationShifts(lowRegistrationPage);
      }

      toast.success('Đã từ chối ca');
    } catch (error) {
      toast.error(
        `Đã từ chối ca nhưng tải lại danh sách thất bại: ${getErrorMessage(error) || 'lỗi không rõ'}`
      );
    } finally {
      setLoading(false);
    }
    return done;
  };

  const handleRevokeApproval = async (shiftId) => {
    let done = false;
    try {
      setLoading(true);
      await shiftAPI.revokeApproval(shiftId);
      done = true;

      // Reload data after revoke approval
      const { from, to } = getDateRange();

      const shiftsResponse = await withBranch(shiftAPI.getShifts, {
        date_from: from,
        date_to: to,
        page: 1,
        limit: 1000,
        status: statusFilter || '',
      });

      const mapped = (shiftsResponse.data || [])
        .map((s) => {
          const date = moment(s.start_time).format('YYYY-MM-DD');
          const time = `Ca ${moment(s.start_time).format('HH:mm')}`;
          const start_ts = moment(s.start_time).valueOf();

          return {
            id: s.id,
            date,
            type: s.shift_type || 'NORMAL',
            time,
            status: s.status,
            employee_id: s.employee_id,
            employee_name: s.employee_name || 'Chưa xác định',
            template_id: s.template_id,
            template_name: s.template_name || s.shift_type,
            start_ts,
            is_preferred: s.is_preferred || false,
            approved_shifts: s.approved_shifts || 0,
            pending_shifts: s.pending_shifts || 0,
            rejected_shifts: s.rejected_shifts || 0,
          };
        })
        .sort((a, b) => {
          // Sắp xếp: ca ưu tiên lên đầu, sau đó theo thời gian
          if (a.is_preferred !== b.is_preferred) {
            return b.is_preferred ? 1 : -1;
          }
          return a.start_ts - b.start_ts;
        });

      setShifts(mapped);
      const pending = mapped.filter((s) => s.status === 'PENDING');
      setPendingShifts(pending);

      toast.success('Đã hủy duyệt ca thành công');
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Hủy duyệt ca thất bại');
    } finally {
      setLoading(false);
    }
    return done;
  };

  // #191746 — góp ý Trợ năng "thêm nút huỷ lệnh từ chối": bấm nhầm "Từ chối" thì đưa ca về lại
  // chờ duyệt (máy chủ tự trả về "chờ xác nhận" nếu ca đã chấm công ra) để quyết lại.
  const handleUndoRejectShift = async (shiftId) => {
    try {
      setLoading(true);
      const undone = await shiftAPI.undoRejectShift(
        shiftId,
        getUserFromLocalStorage()?.id,
      );
      // Máy chủ đưa ca đã chấm công ra về "Chờ xác nhận" (COMPLETED_WAIT_APPROVE), còn lại về
      // "Chờ duyệt" — thông báo phải nói đúng chỗ ca vừa về.
      const undoneStatus = undone?.status ?? undone?.data?.status;

      // Nạp lại đúng kỳ đang xem (cùng getDateRange + statusFilter với các nút Duyệt/Từ chối/Hủy
      // duyệt) để badge và số đếm Đã duyệt/Chờ duyệt/Từ chối trên thẻ lấy lại từ máy chủ.
      const { from, to } = getDateRange();
      const shiftsResponse = await withBranch(shiftAPI.getShifts, {
        date_from: from,
        date_to: to,
        page: 1,
        limit: 1000,
        status: statusFilter || '',
      });
      const mapped = (shiftsResponse.data || [])
        .map((s) => ({
          id: s.id,
          date: moment(s.start_time).format('YYYY-MM-DD'),
          type: s.shift_type || 'NORMAL',
          time: `Ca ${moment(s.start_time).format('HH:mm')}`,
          status: s.status,
          employee_id: s.employee_id,
          employee_name: s.employee_name || 'Chưa xác định',
          template_id: s.template_id,
          template_name: s.template_name || s.shift_type,
          start_ts: moment(s.start_time).valueOf(),
          is_preferred: s.is_preferred || false,
          approved_shifts: s.approved_shifts || 0,
          pending_shifts: s.pending_shifts || 0,
          rejected_shifts: s.rejected_shifts || 0,
        }))
        .sort((a, b) => {
          if (a.is_preferred !== b.is_preferred) {
            return b.is_preferred ? 1 : -1;
          }
          return a.start_ts - b.start_ts;
        });
      setShifts(mapped);
      setPendingShifts(mapped.filter((s) => s.status === 'PENDING'));

      if (activeTab === 'low-registration') {
        await loadLowRegistrationShifts(lowRegistrationPage);
      }
      toast.success(
        undoneStatus === 'COMPLETED_WAIT_APPROVE'
          ? 'Đã hủy từ chối — ca trở lại chờ xác nhận'
          : 'Đã hủy từ chối — ca trở lại chờ duyệt',
      );
    } catch (error) {
      // Câu lỗi trùng giờ có kèm khung giờ ca bị trùng — autoClose chung của app (500ms) tắt quá
      // nhanh để quản lý đọc kịp, nên riêng lỗi này để 10 giây như lỗi Duyệt hàng loạt.
      toast.error(getErrorMessage(error) || 'Hủy từ chối ca thất bại', { autoClose: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    try {
      setLoading(true);
      const bulkRes = await withBranch(shiftAPI.createBulkShifts, bulkFormData);

      // #005135 — /shifts/bulk luôn HTTP 200 và báo lỗi THEO TỪNG DÒNG trong `results`.
      // Không đọc `results` thì mọi dòng bị từ chối (vd NV còn trong tháng đầu tập việc nên
      // chưa nhận được ca chính) sẽ biến mất sau câu "Tạo ca hàng loạt thành công".
      const bulkFailed = (Array.isArray(bulkRes?.results) ? bulkRes.results : []).filter(
        (r) => !r?.success,
      );

      // Reload data after bulk creation
      const { from, to } = getDateRange();

      const shiftsResponse = await withBranch(shiftAPI.getShifts, {
        date_from: from,
        date_to: to,
        page: 1,
        limit: 1000,
      });

      const mapped = (shiftsResponse.data || []).map((s) => {
        const date = moment(s.start_time).format('YYYY-MM-DD');
        const time = `Ca ${moment(s.start_time).format('HH:mm')}`;

        return {
          id: s.id,
          date,
          type: s.shift_type || 'NORMAL',
          time,
          status: s.status,
          employee_id: s.employee_id,
          employee_name: s.employee_name || 'Chưa xác định',
          template_id: s.template_id,
          template_name: s.template_name || s.shift_type,
          approved_shifts: s.approved_shifts || 0,
          pending_shifts: s.pending_shifts || 0,
          rejected_shifts: s.rejected_shifts || 0,
        };
      });

      setShifts(mapped);
      const pending = mapped.filter((s) => s.status === 'PENDING');
      setPendingShifts(pending);

      setShowBulkModal(false);
      if (bulkFailed.length > 0) {
        bulkFailed.forEach((r) =>
          toast.error(
            `Dòng ${(r.index ?? 0) + 1}: ${r.error || 'Không tạo được ca'}`,
            { autoClose: 8000 },
          ),
        );
      } else {
        toast.success('Tạo ca hàng loạt thành công');
      }
    } catch (error) {
      toast.error(error?.message || 'Tạo ca hàng loạt thất bại');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBranchName(selectedBranch?.name || '');

    const loadData = async () => {
      try {
        // Load shifts
        const { from, to } = getDateRange();

        const shiftsResponse = await withBranch(shiftAPI.getShifts, {
          date_from: from,
          date_to: to,
          page: 1,
          limit: 1000,
          status: statusFilter || '',
        });

        const mapped = (shiftsResponse.data || [])
          .map((s) => {
            const date = moment(s.start_time).format('YYYY-MM-DD');
            const time = `Ca ${moment(s.start_time).format('HH:mm')}`;
            const start_ts = moment(s.start_time).valueOf();

            return {
              id: s.id,
              date,
              type: s.shift_type || 'NORMAL',
              time,
              status: s.status,
              employee_id: s.employee_id,
              employee_name: s.employee_name || 'Chưa xác định',
              template_id: s.template_id,
              template_name: s.template_name || s.shift_type,
              start_ts,
              is_preferred: s.is_preferred || false,
              approved_shifts: s.approved_shifts || 0,
              pending_shifts: s.pending_shifts || 0,
              rejected_shifts: s.rejected_shifts || 0,
            };
          })
          .sort((a, b) => {
            // Sắp xếp: ca ưu tiên lên đầu, sau đó theo thời gian
            if (a.is_preferred !== b.is_preferred) {
              return b.is_preferred ? 1 : -1;
            }
            return a.start_ts - b.start_ts;
          });

        setShifts(mapped);
        const pending = mapped.filter((s) => s.status === 'PENDING');
        setPendingShifts(pending);

        // Load employees
        const employeesResponse = await withBranch(employeeAPI.getEmployees, {
          page: 1,
          limit: 100,
        });
        setEmployees(employeesResponse.data || []);

        // Load templates
        // #015110 — tên hàm đúng là `getShiftTemplates`; `getTemplates` không tồn tại nên cả
        // khối loadData này chết giữa chừng ("apiCall is not a function") và danh sách mẫu ca
        // LUÔN rỗng — modal sửa ca không có gì để chọn.
        const templatesResponse = await withBranch(shiftTemplateAPI.getShiftTemplates, {});
        setTemplates(templatesResponse.data || []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [
    selectedBranch?.id,
    currentDate,
    selectedBranch?.name,
    withBranch,
    statusFilter,
    viewMode,
    getDateRange,
  ]);

  // Báo cáo chi tiết ca đã được di chuyển sang ShiftDayDetailModal

  /**
   * Khung LỊCH TUẦN dùng chung (7 Card xếp dọc theo ngày).
   *
   * #015110 — nhận tham số thay vì đọc thẳng state của tab "Lịch ca": bảng ca ĐỀ XUẤT của bot
   * dùng lại đúng khung này (ràng buộc của yêu cầu: "không vẽ bảng tuần thứ hai"), chỉ khác
   * nguồn dữ liệu, mốc tuần và hành động trên từng ca.
   *
   * @param weekStart    Ngày mốc của tuần (mặc định: tuần đang xem ở tab Lịch ca).
   * @param shiftsForDay (dateStr) => ca của ngày đó; mặc định lấy từ state `shifts`.
   * @param onDayClick   Bấm vào cả thẻ ngày; null = thẻ ngày không bấm được.
   * @param onShiftClick Bấm vào 1 ca; có thì mỗi ca thành nút bấm được.
   * @param shiftExtra   Render thêm phía sau mỗi ca (vd nút Từ chối của bảng đề xuất).
   * @param dayExtra     Render thêm ở CUỐI mỗi ngày, nhận `dateStr` (YYYY-MM-DD). #180848 —
   *                     `shiftExtra` chỉ treo được vào 1 CA đã có người nên không nói được "ngày
   *                     này còn thiếu người" ở slot CHƯA AI nhận (ngày đó có thể không có ca nào
   *                     để treo). Vẫn dùng đúng 1 khung tuần, không vẽ bảng thứ hai (#015110).
   * @param shiftBelow   Render thêm NGAY DƯỚI mỗi ca, xuống dòng riêng (#001848: dải tên người
   *                     cùng đăng ký ca đó ở bảng đề xuất).
   * @param emptyDayText Câu hiện ở ngày không có ca nào.
   */
  const renderWeekView = ({
    weekStart = getStartOfWeek(currentDate),
    shiftsForDay = getShiftsForDate,
    onDayClick = handleDateClickFromObj,
    onShiftClick = null,
    shiftExtra = null,
    dayExtra = null,
    shiftBelow = null,
    emptyDayText = 'Chưa có ca',
  } = {}) => {
    const startOfWeek = getStartOfWeek(weekStart);
    const days = [];
    const dayNames = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ Nhật'];

    for (let i = 0; i < 7; i++) {
      const date = moment(startOfWeek).add(i, 'days').toDate();
      const dateStr = formatDateFromObj(date);
      const dayShifts = shiftsForDay(dateStr);
      const isToday = moment(date).isSame(moment(), 'day');

      days.push(
        <Col
          key={i}
          md={12}
          className="mb-3"
        >
          <Card
            className={`h-100 ${isToday ? 'border-primary' : ''}`}
            onClick={onDayClick ? () => onDayClick(date) : undefined}
            style={onDayClick ? { cursor: 'pointer' } : undefined}
          >
            <Card.Body>
              <Row className="align-items-center">
                <Col xs={3}>
                  <div className="text-center">
                    <div className="text-muted small mb-1">{dayNames[i]}</div>
                    <div className="h3 mb-1">{date.getDate()}</div>
                    <div className="text-muted small">Tháng {date.getMonth() + 1}</div>
                  </div>
                </Col>
                <Col xs={9}>
                  {dayShifts.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {dayShifts.map((shift, idx) => {
                        const { color: variant, label: statusLabel } = getStatusConfig(shift.status);

                        // .badge của Bootstrap mặc định white-space:nowrap nên chip nhiều mảnh chữ
                        // không xuống dòng được -> tràn ngang cả trang ở khổ điện thoại (#023251).
                        // text-wrap + flex-wrap trả lại khả năng xuống dòng, mw-100 chặn chip rộng
                        // hơn cột chứa nó.
                        const badge = (
                          <Badge
                            bg={variant}
                            className="d-flex flex-wrap align-items-center gap-2 p-2 text-wrap text-start mw-100"
                            data-testid={onShiftClick ? `week-shift-${shift.id}` : undefined}
                            role={onShiftClick ? 'button' : undefined}
                            style={{
                              whiteSpace: 'normal',
                              flexWrap: 'wrap',
                              minWidth: 0,
                              ...(onShiftClick ? { cursor: 'pointer' } : {}),
                            }}
                            onClick={
                              onShiftClick
                                ? (e) => {
                                    // Thẻ ngày bọc ngoài cũng có onClick ở tab Lịch ca — không
                                    // chặn thì bấm 1 ca sẽ mở nhầm modal chi tiết cả ngày.
                                    e.stopPropagation();
                                    onShiftClick(shift);
                                  }
                                : undefined
                            }
                          >
                            {(shift.status === 'PROPOSED_BOT' || shift.status === 'APPROVE_BOT') && (
                              <Badge
                                bg="light"
                                text="dark"
                                className="small"
                              >
                                🤖
                              </Badge>
                            )}
                            {shift.is_preferred && (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="small"
                              >
                                ⭐ Ưu tiên
                              </Badge>
                            )}
                            <span className="fw-bold text-break">{shift.employee_name}</span>
                            <span className="small text-break">{shift.template_name}</span>
                            <span className="small text-break">{shift.time}</span>
                            <Badge
                              bg="light"
                              text="dark"
                              className="small"
                            >
                              {statusLabel}
                            </Badge>
                          </Badge>
                        );

                        const belowContent = shiftBelow ? shiftBelow(shift) : null;
                        if (!shiftExtra && !belowContent)
                          return <React.Fragment key={idx}>{badge}</React.Fragment>;
                        return (
                          <div
                            key={idx}
                            className="d-flex flex-column mw-100"
                            style={{ minWidth: 0 }}
                          >
                            <div
                              className="d-flex flex-wrap align-items-center gap-1 mw-100"
                              style={{ minWidth: 0 }}
                            >
                              {badge}
                              {shiftExtra ? shiftExtra(shift) : null}
                            </div>
                            {belowContent}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted fst-italic">{emptyDayText}</div>
                  )}
                  {dayExtra ? dayExtra(dateStr) : null}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>,
      );
    }

    return days;
  };

  const renderCalendarDays = () => {
    const days = [];
    const firstDayAdjusted = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    const totalCells = Math.ceil((firstDayAdjusted + daysInMonth) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const day = i - firstDayAdjusted + 1;
      const isValidDay = day > 0 && day <= daysInMonth;
      const dateStr = isValidDay ? formatDate(day) : '';
      const cellDate = isValidDay ? moment(currentDate).date(day).toDate() : null;
      const dayShifts = isValidDay ? getShiftsForDate(dateStr) : [];
      const isToday = isValidDay && moment(cellDate).isSame(moment(), 'day');

      // Kiểm tra coverage cho ngày này (chỉ khi không phải hôm nay và có coverage data)
      const hasShortage = !isToday && coverageData?.datesWithShortage?.has(dateStr);
      const coverageColor = hasShortage
        ? 'border-danger bg-danger-subtle'
        : !isToday && coverageData
          ? 'border-success bg-success-subtle'
          : '';

      days.push(
        <Col
          key={i}
          className="p-1"
          style={{ flex: '0 0 14.2857%', maxWidth: '14.2857%' }}
        >
          <Card
            className={`h-100 ${!isValidDay ? 'invisible' : ''} ${
              isToday ? 'border-primary bg-primary text-white' : coverageColor || ''
            }`}
            onClick={() => isValidDay && handleDateClick(day)}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="p-1 p-md-2">
              {isValidDay && (
                <>
                  <div className="fw-bold mb-2">{day}</div>
                  {/* Khổ điện thoại: ô ngày của lịch THÁNG chỉ rộng ~1/7 màn, không đủ chỗ cho
                      chi tiết ca — chỉ hiện SỐ ca (bấm vào ngày vẫn mở chi tiết như cũ). Chi tiết
                      giữ nguyên từ md trở lên. (#060429 — trước đây chi tiết kéo cả trang tràn
                      ngang ở 390/320px.) */}
                  {!isToday && dayShifts.length > 0 && (
                    <div className="d-md-none">
                      <Badge
                        bg="primary"
                        className="small w-100 text-wrap"
                        style={{ whiteSpace: 'normal' }}
                        title={`${dayShifts.length} ca trong ngày`}
                      >
                        {dayShifts.length}
                      </Badge>
                    </div>
                  )}
                  {!isToday && dayShifts.length > 0 && (
                    <div className="d-none d-md-flex flex-column gap-1">
                      {dayShifts.slice(0, 3).map((shift, idx) => {
                        const { color: variant, label: statusLabel } = getStatusConfig(shift.status);

                        return (
                          // Cùng lỗi với chip ở renderWeekView (#023251): .badge mặc định
                          // white-space:nowrap nên tên NV dài kéo ô ngày rộng ra -> lịch THÁNG
                          // tràn ngang ở khổ điện thoại.
                          <Badge
                            key={idx}
                            bg={variant}
                            className="small text-wrap text-break text-start w-100"
                            style={{ whiteSpace: 'normal', minWidth: 0 }}
                          >
                            {shift.is_preferred && <div className="fw-bold text-break">⭐ Ưu tiên</div>}
                            <div className="fw-bold text-break">{shift.employee_name}</div>
                            <div className="text-break">{shift.template_name}</div>
                            <div className="text-break">{shift.time}</div>
                          </Badge>
                        );
                      })}
                      {dayShifts.length > 3 && <div className="small text-muted">+{dayShifts.length - 3} ca khác</div>}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>,
      );
    }

    return days;
  };

  const selectedDateShifts = selectedDate ? getShiftsForDate(selectedDate) : [];
  const approvedShifts = shifts.filter((s) => s.status === 'APPROVED' || s.status === 'APPROVE_BOT');
  const filteredPendingShifts = preferredFilter ? pendingShifts.filter((s) => s.is_preferred) : pendingShifts;

  // Tính toán date range cho low registration để hiển thị
  const lowRegistrationDateRange = useMemo(() => {
    return getLowRegistrationDateRange();
  }, [getLowRegistrationDateRange]);

  // Nhóm các ca có ít đăng ký theo template và ngày
  const groupedLowRegistrationShifts = useMemo(() => {
    const groups = {};
    lowRegistrationShifts.forEach((shift) => {
      const date = moment(shift.start_time).format('YYYY-MM-DD');
      const templateName = shift.template_name || 'Chưa xác định';
      const key = `${templateName}_${date}`;

      if (!groups[key]) {
        groups[key] = {
          templateName,
          date,
          dateFormatted: moment(shift.start_time).format('DD/MM/YYYY'),
          shifts: [],
          totalRegistration: 0,
          totalMaxEmployees: 0,
        };
      }

      const registrationCount = shift.registration_count || 0;
      const maxEmployees = shift.max_employees || 0;

      groups[key].shifts.push(shift);
      groups[key].totalRegistration += registrationCount;
      groups[key].totalMaxEmployees += maxEmployees;
    });

    // Sắp xếp các nhóm: ưu tiên nhóm có ít đăng ký nhất, sau đó theo ngày
    return Object.values(groups).sort((a, b) => {
      const aShortage = a.totalMaxEmployees - a.totalRegistration;
      const bShortage = b.totalMaxEmployees - b.totalRegistration;
      if (aShortage !== bShortage) {
        return bShortage - aShortage; // Nhóm thiếu nhiều người hơn lên đầu
      }
      return moment(a.date).valueOf() - moment(b.date).valueOf(); // Sau đó sắp xếp theo ngày
    });
  }, [lowRegistrationShifts]);


  return (
    <PageLayout>
      <div className="mb-4">
        <Row className="align-items-center mb-4">
          <Col>
            <h2 className="mb-0">Quản lý Ca Làm - {branchName}</h2>
          </Col>
          <Col xs="auto">
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '200px' }}
              >
                <option value="">Tất cả trạng thái</option>
                <option value="PENDING">Chờ duyệt</option>
                <option value="APPROVED">Đã duyệt</option>
                <option value="PROPOSED_BOT">🤖 Bot đề xuất</option>
                <option value="APPROVE_BOT">Đã duyệt (bot cũ)</option>
                <option value="IN_PROGRESS">Đang làm</option>
                <option value="COMPLETED">Hoàn thành</option>
                <option value="REJECTED">Từ chối</option>
                <option value="CANCELLED">Đã hủy</option>
              </Form.Select>
              <Button
                variant="outline-info"
                onClick={handleAutoApprove}
                disabled={autoApproveLoading}
                className="d-flex align-items-center gap-2"
                title="Tự động duyệt ca PENDING tuần tới theo thuật toán bot"
              >
                <Robot size={16} />
                {autoApproveLoading ? 'Đang chạy...' : 'Auto Approve'}
              </Button>
              {isAdminUser && (
                <Button
                  variant="danger"
                  onClick={() => setCloseWeekTarget('')}
                  title="Duyệt ca xong thì bấm: chốt bảng xếp hạng + cộng thưởng top, trừ TLT người không đạt điều kiện đăng ký ca"
                >
                  🔒 Chốt tuần (thưởng + phạt)
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />

        <Tab.Container
          activeKey={activeTab}
          onSelect={setActiveTab}
        >
          <Nav
            variant="tabs"
            className="mb-4"
          >
            <Nav.Item>
              <Nav.Link
                eventKey="calendar"
                className="d-flex align-items-center gap-2"
              >
                <Calendar size={16} />
                Lịch ca
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link
                eventKey="overtime"
                className="d-flex align-items-center gap-2"
              >
                <Clock size={16} />
                Duyệt ca tăng cường
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link
                eventKey="low-registration"
                className="d-flex align-items-center gap-2"
              >
                <ExclamationTriangle size={16} />
                Ca ít đăng ký ({lowRegistrationShifts.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="empty-slots"
                className="d-flex align-items-center gap-2"
              >
                <LightningCharge size={16} />
                Ca trống tuần tới ({emptySlots.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="bot-review"
                className="d-flex align-items-center gap-2"
              >
                <Robot size={16} />
                Đề xuất của bot ({botShifts.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="leaderboard"
                className="d-flex align-items-center gap-2"
              >
                <Trophy size={16} />
                Xếp hạng đóng góp ca
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="calendar">
              <Card className="mb-4">
                <Card.Body>
                  <Row className="align-items-center mb-3">
                    <Col xs="auto">
                      <div
                        className="btn-group"
                        role="group"
                      >
                        <Button
                          variant={viewMode === 'week' ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setViewMode('week')}
                          className="d-flex align-items-center gap-2"
                        >
                          <Calendar size={16} />
                          Tuần
                        </Button>
                        <Button
                          variant={viewMode === 'month' ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setViewMode('month')}
                          className="d-flex align-items-center gap-2"
                        >
                          <Calendar size={16} />
                          Tháng
                        </Button>
                      </div>
                    </Col>
                    <Col className="text-center">
                      <h4 className="mb-0">
                        {viewMode === 'week'
                          ? `Tuần ${getWeekNumber(currentDate)} - ${
                              monthNames[currentDate.getMonth()]
                            } ${currentDate.getFullYear()}`
                          : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
                      </h4>
                    </Col>
                    <Col xs="auto">
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant={preferredFilter ? 'warning' : 'outline-secondary'}
                          onClick={() => setPreferredFilter((v) => !v)}
                          className="d-flex align-items-center gap-1"
                          title="Chỉ hiển thị ca ưu tiên"
                        >
                          ⭐ Ưu tiên
                        </Button>
                        <div
                          className="btn-group"
                          role="group"
                          aria-label="Chế độ hiển thị theo loại ca"
                        >
                          {SHIFT_TYPE_VIEW_MODES.map((mode) => (
                            <Button
                              key={mode.value}
                              size="sm"
                              variant={statusFilter === mode.value ? 'primary' : 'outline-secondary'}
                              onClick={() => setStatusFilter(mode.value)}
                              title={mode.title}
                            >
                              {mode.label}
                            </Button>
                          ))}
                        </div>
                        {viewMode === 'month' && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => checkCoverage(true)}
                            disabled={coverageLoading}
                            className="d-flex align-items-center gap-1"
                            title="Kiểm tra lại coverage"
                          >
                            <ArrowClockwise
                              size={16}
                              className={coverageLoading ? 'spinning' : ''}
                            />
                            {coverageLoading ? 'Đang kiểm tra...' : 'Kiểm tra coverage'}
                          </Button>
                        )}
                        <div
                          className="btn-group"
                          role="group"
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={viewMode === 'week' ? goToPreviousWeek : goToPreviousMonth}
                          >
                            <ChevronLeft size={16} />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={viewMode === 'week' ? goToNextWeek : goToNextMonth}
                          >
                            <ChevronRight size={16} />
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {viewMode === 'week' ? (
                    <Row>{renderWeekView()}</Row>
                  ) : (
                    <>
                      <Row className="mb-2">
                        {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((day) => (
                          <Col
                            key={day}
                            className="text-center"
                            style={{ flex: '0 0 14.2857%', maxWidth: '14.2857%' }}
                          >
                            <div className="fw-bold text-muted">{day}</div>
                          </Col>
                        ))}
                      </Row>
                      <Row>{renderCalendarDays()}</Row>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="overtime">
              <OvertimeApprovalTab
                isActive={activeTab === 'overtime'}
                withBranch={withBranch}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="low-registration">
              <Card>
                <Card.Body>
                  <Row className="align-items-center mb-4">
                    <Col>
                      <h4 className="mb-0">
                        Ca có ít đăng ký ({lowRegistrationShifts.length} ca, {groupedLowRegistrationShifts.length} nhóm)
                      </h4>
                      <div className="text-muted small mt-1">
                        Các ca đang chờ duyệt có ít người đăng ký, cần được xem xét ưu tiên
                        <span className="ms-2">
                          • Khoảng thời gian: {moment(lowRegistrationDateRange.dateFrom).format('DD/MM/YYYY')} -{' '}
                          {moment(lowRegistrationDateRange.dateTo).format('DD/MM/YYYY')}
                        </span>
                      </div>
                    </Col>
                    <Col xs="auto">
                      <div className="d-flex align-items-center gap-2">
                        <Form.Select
                          size="sm"
                          value={lowRegistrationPeriod}
                          onChange={(e) => {
                            setLowRegistrationPeriod(e.target.value);
                            setLowRegistrationPage(1);
                          }}
                          style={{ width: '200px' }}
                        >
                          <option value="current-month">Tháng hiện tại</option>
                          <option value="next-month">Tháng tiếp theo</option>
                          <option value="next-week">Tuần tiếp theo</option>
                        </Form.Select>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => loadLowRegistrationShifts(lowRegistrationPage)}
                          disabled={lowRegistrationLoading}
                          className="d-flex align-items-center gap-2"
                        >
                          <ArrowClockwise
                            size={16}
                            className={lowRegistrationLoading ? 'spinning' : ''}
                          />
                          Làm mới
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {lowRegistrationLoading ? (
                    <div className="text-center py-5 text-muted">Đang tải...</div>
                  ) : groupedLowRegistrationShifts.length === 0 ? (
                    <div className="text-center py-5 text-muted">Không có ca nào có ít đăng ký</div>
                  ) : (
                    <div className="d-flex flex-column gap-4">
                      {groupedLowRegistrationShifts.map((group, groupIdx) => {
                        const groupShortage = group.totalMaxEmployees - group.totalRegistration;
                        const isLowRegistration = groupShortage > 0;

                        return (
                          <Card
                            key={`${group.templateName}_${group.date}_${groupIdx}`}
                            className={`${isLowRegistration ? 'border-danger border-2' : 'border-warning'}`}
                          >
                            <Card.Header className="bg-light">
                              <Row className="align-items-center">
                                <Col>
                                  <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0">{group.templateName}</h5>
                                    <Badge bg="secondary">{group.dateFormatted}</Badge>
                                    {isLowRegistration && (
                                      <Badge bg="danger">
                                        <ExclamationTriangle
                                          size={12}
                                          className="me-1"
                                        />
                                        Thiếu {groupShortage} người
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-muted small mt-1">
                                    Tổng: {group.shifts[0].registration_count}/{group.shifts[0].max_employees} đăng ký •{' '}
                                    {group.shifts.length} ca
                                  </div>
                                </Col>
                              </Row>
                            </Card.Header>
                            <Card.Body>
                              <div className="d-flex flex-column gap-2">
                                {group.shifts.map((shift) => {
                                  const registrationCount = shift.registration_count || 0;
                                  const maxEmployees = shift.max_employees || 0;
                                  const shiftShortage = maxEmployees - registrationCount;
                                  const isShiftLow = shiftShortage > 0;
                                  const approvedCount = shift.approved_shifts || 0;
                                  const pendingCount = shift.pending_shifts || 0;
                                  const rejectedCount = shift.rejected_shifts || 0;

                                  return (
                                    <Card
                                      key={shift.id}
                                      className={`${isShiftLow ? 'border-danger' : 'border-secondary'}`}
                                    >
                                      <Card.Body className="py-2">
                                        <Row className="align-items-center">
                                          <Col>
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                              <div className="fw-bold">{shift.employee_name || 'Chưa xác định'}</div>
                                              {shift.is_preferred && <Badge bg="info">⭐ Ca ưu tiên</Badge>}
                                              {isShiftLow && <Badge bg="warning">Thiếu {shiftShortage} người</Badge>}
                                              <NewHireCashierBadge shift={shift} />
                                            </div>
                                            <div className="text-muted small mb-1">
                                              {moment(shift.start_time).format('HH:mm')} -{' '}
                                              {moment(shift.end_time).format('HH:mm')}
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <Badge
                                                bg="light"
                                                text="dark"
                                              >
                                                {shift.status}
                                              </Badge>
                                            </div>
                                            {(approvedCount || pendingCount || rejectedCount) && (
                                              <div className="text-muted small mt-1">
                                                <span className="me-2">✅ {approvedCount} Đã duyệt</span>
                                                <span className="me-2">⏳ {pendingCount} Chờ duyệt</span>
                                                <span>❌ {rejectedCount} Từ chối</span>
                                              </div>
                                            )}
                                          </Col>
                                          <Col xs="auto">
                                            <div className="d-flex flex-wrap gap-2">
                                              <Button
                                                size="sm"
                                                variant="success"
                                                onClick={() => handleApproveShift(shift.id)}
                                                disabled={loading}
                                                className="d-flex align-items-center gap-1"
                                              >
                                                <CheckCircle size={16} />
                                                Duyệt
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => handleRejectShift(shift.id)}
                                                disabled={loading}
                                                className="d-flex align-items-center gap-1"
                                              >
                                                <XCircle size={16} />
                                                Từ chối
                                              </Button>
                                            </div>
                                          </Col>
                                        </Row>
                                      </Card.Body>
                                    </Card>
                                  );
                                })}
                              </div>
                            </Card.Body>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {!lowRegistrationLoading && groupedLowRegistrationShifts.length > 0 && (
                    <div className="mt-4">
                      <PaginationComponent
                        totalItems={lowRegistrationTotal}
                        itemsPerPage={lowRegistrationLimit}
                        currentPage={lowRegistrationPage}
                        onPageChange={handleLowRegistrationPageChange}
                        loading={lowRegistrationLoading}
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="empty-slots">
              <Card>
                <Card.Body>
                  <Row className="align-items-center mb-4">
                    <Col>
                      <h4 className="mb-0">Ca trống ({emptySlots.length})</h4>
                      <div className="text-muted small mt-1">{weekInfo.displayText}</div>
                    </Col>
                    <Col xs="auto">
                      <div className="d-flex align-items-center gap-2">
                        <div className="btn-group">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={goToPreviousWeekForEmptySlots}
                            disabled={emptySlotsLoading}
                          >
                            <ChevronLeft size={16} />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={goToNextWeekForEmptySlots}
                            disabled={emptySlotsLoading}
                          >
                            <ChevronRight size={16} />
                          </Button>
                        </div>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={goToCurrentWeekForEmptySlots}
                          disabled={emptySlotsLoading}
                        >
                          Tuần này
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={goToNextWeekFromNowForEmptySlots}
                          disabled={emptySlotsLoading}
                        >
                          Tuần tới
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={loadEmptySlots}
                          disabled={emptySlotsLoading}
                          className="d-flex align-items-center gap-2"
                        >
                          <ArrowClockwise
                            size={16}
                            className={emptySlotsLoading ? 'spinning' : ''}
                          />
                          Làm mới
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {emptySlotsLoading ? (
                    <div className="text-center py-5 text-muted">Đang tải...</div>
                  ) : emptySlots.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      Không có ca trống nào trong {weekInfo.displayText}
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {emptySlots.map((slot, idx) => {
                        const shortage = slot.max_employees - slot.current_employees;
                        const startTime = moment(slot.start_time);
                        const endTime = moment(slot.end_time);

                        return (
                          <Card
                            key={`${slot.template_id}-${slot.date}-${idx}`}
                            className={slot.has_boost ? 'border-warning border-2' : 'border-secondary'}
                          >
                            <Card.Body>
                              <Row className="align-items-center">
                                <Col>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <h5 className="mb-0">{slot.template_name}</h5>
                                    {slot.has_boost && (
                                      <Badge bg="warning">⚡ Boost {slot.boost_reward_rate_bonus}%</Badge>
                                    )}
                                    <Badge bg="danger">Thiếu {shortage} người</Badge>
                                  </div>
                                  <div className="text-muted mb-1">{moment(slot.date).format('dddd, DD/MM/YYYY')}</div>
                                  <div className="text-muted mb-2">
                                    {startTime.format('HH:mm')} - {endTime.format('HH:mm')}
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge bg={slot.current_employees < slot.max_employees ? 'warning' : 'success'}>
                                      {slot.current_employees}/{slot.max_employees} người
                                    </Badge>
                                  </div>
                                </Col>
                                <Col xs="auto">
                                  <div className="d-flex flex-wrap gap-2">
                                    <Button
                                      size="sm"
                                      variant={slot.has_boost ? 'outline-warning' : 'outline-primary'}
                                      onClick={() => openBoostModal(slot)}
                                      disabled={loading}
                                      className="d-flex align-items-center gap-1"
                                    >
                                      <LightningCharge size={16} />
                                      {slot.has_boost ? 'Sửa boost' : 'Set boost'}
                                    </Button>
                                    {slot.has_boost && (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => handleRemoveBoost(slot)}
                                        disabled={loading}
                                        className="d-flex align-items-center gap-1"
                                      >
                                        <XCircle size={16} />
                                        Xóa boost
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* ── Tab: Bot Review ───────────────────────────────────────── */}
            <Tab.Pane eventKey="bot-review">
              <Card>
                <Card.Body>
                  <Row className="align-items-center mb-4">
                    <Col>
                      <h4 className="mb-0">
                        <Robot
                          size={20}
                          className="me-2 text-info"
                        />
                        Đề xuất của bot — {botWeekInfo.label} ({botShifts.length} đề xuất)
                      </h4>
                      <div className="text-muted small mt-1">
                        Bot chỉ ĐỀ XUẤT — ca ở đây <strong>chưa được duyệt</strong>: không tính vào bộ đếm "Đã
                        duyệt", chưa chiếm slot, nhân viên chưa nhận được ca. Bấm <strong>Approve</strong> thì ca mới
                        thành ca duyệt thật (ghi đủ chữ ký người duyệt, y hệt duyệt tay).
                      </div>
                    </Col>
                    <Col xs="auto">
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={handleAutoApprove}
                          disabled={autoApproveLoading}
                          className="d-flex align-items-center gap-1"
                        >
                          <Robot size={14} />
                          {autoApproveLoading ? 'Đang chạy...' : 'Chạy lại Auto Approve'}
                        </Button>
                        {/* #015110 — 1 API transaction thay cho N lần gọi rời từ trình duyệt:
                            hỏng giữa chừng không còn để lại nửa duyệt nửa chưa. */}
                        <Button
                          data-testid="bot-bulk-approve"
                          variant="outline-success"
                          size="sm"
                          onClick={handleBulkApproveProposedWeek}
                          disabled={loading || botShiftsLoading || bulkApproving || botShifts.length === 0}
                          className="d-flex align-items-center gap-1"
                          title="Duyệt toàn bộ ca đề xuất đang hiển thị của tuần này (1 giao dịch)"
                        >
                          <CheckCircle size={14} />
                          {bulkApproving ? 'Đang duyệt...' : 'Duyệt tất cả'}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRevokeAllBotShifts}
                          disabled={loading || botShiftsLoading || botShifts.length === 0}
                          className="d-flex align-items-center gap-1"
                        >
                          <XCircle size={14} />
                          Revoke All Bot
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleRevokeAllApprovedShifts}
                          disabled={loading || botShiftsLoading}
                          className="d-flex align-items-center gap-1"
                          title="Revoke cả ca Bot duyệt lẫn ca duyệt tay (APPROVED) — dùng để reset sạch tuần khi test"
                        >
                          <XCircle size={14} />
                          Revoke All (kể cả duyệt tay)
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={loadBotShifts}
                          disabled={botShiftsLoading}
                          className="d-flex align-items-center gap-1"
                        >
                          <ArrowClockwise
                            size={14}
                            className={botShiftsLoading ? 'spinning' : ''}
                          />
                          Làm mới
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* #180331 — ĐỀ XUẤT KHÔNG CÒN HỢP LỆ (mẫu ca đã tắt/đổi ngày): không nằm trong
                      lô duyệt (nên "Duyệt tất cả" không còn kẹt), nhưng KHÔNG im lặng biến mất —
                      hiện rõ ca nào / của ai / ngày giờ nào / vì sao, và KHÔNG tự xoá. */}
                  {staleBotShifts.length > 0 && (
                    <div
                      className="mb-3 rounded border border-warning bg-white p-2"
                      data-testid="bot-stale-proposals"
                    >
                      <div className="small fw-semibold mb-1 text-danger">
                        ⚠️ {staleBotShifts.length} đề xuất không còn hợp lệ — không đưa vào lô duyệt
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {staleBotShifts.slice(0, 10).map((s) => (
                          <div key={`stale-${s.id}`} className="small text-muted">
                            {moment(s.start_time).format('ddd DD/MM HH:mm')} —{' '}
                            {s.template_name || s.template_id} — {s.employee_name || s.employee_id}:{' '}
                            {s.stale_reason || 'Mẫu ca đã bị vô hiệu hóa'}
                          </div>
                        ))}
                        {staleBotShifts.length > 10 && (
                          <div className="small text-muted">
                            ... và {staleBotShifts.length - 10} đề xuất không còn hợp lệ khác
                          </div>
                        )}
                      </div>
                      <div className="small text-muted mt-1">
                        Các ca này vẫn được giữ nguyên (không xoá). Muốn xếp lại thì mở lại mẫu ca ở
                        "Cấu hình ca làm việc", hoặc dùng nút "Revoke All Bot" để trả về chờ duyệt.
                      </div>
                    </div>
                  )}

                  {/* #015110 — chọn TUẦN + kiểu xem cho bảng ca đề xuất */}
                  <Row className="align-items-center mb-3">
                    <Col xs="auto">
                      <div
                        className="btn-group"
                        role="group"
                      >
                        <Button
                          data-testid="bot-view-week"
                          variant={botViewMode === 'week' ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setBotViewMode('week')}
                          className="d-flex align-items-center gap-1"
                        >
                          <Calendar size={14} />
                          Lịch tuần
                        </Button>
                        <Button
                          data-testid="bot-view-list"
                          variant={botViewMode === 'list' ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setBotViewMode('list')}
                          className="d-flex align-items-center gap-1"
                        >
                          <FileText size={14} />
                          Danh sách
                        </Button>
                      </div>
                    </Col>
                    <Col className="text-center">
                      <span
                        className="fw-semibold"
                        data-testid="bot-week-label"
                      >
                        {botWeekInfo.label}
                      </span>
                    </Col>
                    <Col xs="auto">
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setBotWeekStart(moment().add(1, 'week').startOf('isoWeek'))}
                        >
                          Tuần tới
                        </Button>
                        <div
                          className="btn-group"
                          role="group"
                        >
                          <Button
                            data-testid="bot-week-prev"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() =>
                              setBotWeekStart(moment(botWeekStart).subtract(1, 'week').startOf('isoWeek'))
                            }
                          >
                            <ChevronLeft size={16} />
                          </Button>
                          <Button
                            data-testid="bot-week-next"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setBotWeekStart(moment(botWeekStart).add(1, 'week').startOf('isoWeek'))}
                          >
                            <ChevronRight size={16} />
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* #133908 — bộ yếu tố phải nằm NGAY TRÊN khối phương án, vì các phương án
                      bên dưới chính là kết quả của bộ yếu tố vừa chốt. */}
                  <BotFactorControlPanel branchId={selectedBranch?.id} onSaved={loadBotProposals} />

                  <div
                    className="mb-3 rounded border bg-light p-3"
                    data-testid="bot-multi-option-box"
                  >
                    <div className="mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold">Bot Review Multi-option (đề xuất)</span>
                        {/* #180848 — khối này nay hiện ở CẢ Lịch tuần lẫn Danh sách; ở khổ điện
                            thoại nó khá dài nên cho thu gọn, nhưng mặc định luôn MỞ (trước đây
                            quản lý xem Lịch tuần không hề thấy khối này). */}
                        <Button
                          data-testid="bot-multi-option-toggle"
                          variant="link"
                          size="sm"
                          className="p-0 text-decoration-none"
                          aria-expanded={!botOptionsCollapsed}
                          onClick={() => setBotOptionsCollapsed((v) => !v)}
                        >
                          {botOptionsCollapsed ? 'Mở rộng ▾' : 'Thu gọn ▴'}
                        </Button>
                      </div>
                    </div>

                    {/* #182918 — kết quả lần áp dụng vừa rồi + số CÒN LẠI sau áp dụng (thẻ số bên
                        dưới lúc này đã là của đề xuất mới nạp lại, không phải phần còn lại). */}
                    {!botOptionsCollapsed && appliedProposalResult && (
                      <div
                        className="alert alert-success small py-2 px-2 mb-2"
                        data-testid="bot-option-applied-result"
                      >
                        ✅ Đã áp dụng {appliedProposalResult.name} ({appliedProposalResult.at}) —{' '}
                        <strong>{appliedProposalResult.updated}</strong> ca chuyển sang Đề xuất — chưa duyệt ·
                        Còn lại sau áp dụng: thiếu người <strong>{appliedProposalResult.missing}</strong> ca ·
                        trống trong slot có đơn <strong>{appliedProposalResult.emptyInRegistered}</strong>
                      </div>
                    )}

                    {!botOptionsCollapsed && botProposals.length > 0 && (
                      <div className="small text-muted mb-2" data-testid="bot-option-gesture-hint">
                        Chạm để xem số · chạm đúp để áp dụng
                      </div>
                    )}

                    {!botOptionsCollapsed && botProposals.length === 0 && (
                      <div className="small text-muted mb-2" data-testid="bot-option-empty">
                        {proposalLoading
                          ? 'Đang tạo phương án...'
                          : proposalShortageReason
                            ? `ℹ️ ${proposalShortageReason}`
                            : selectedBranch?.id
                              ? 'ℹ️ Bot chưa sinh được phương án nào cho tuần này — hãy kiểm tra còn ca/đăng ký nào cần xếp người, rồi bấm "Chốt bộ yếu tố & tạo phương án" ở khối phía trên.'
                              : 'ℹ️ Hãy chọn một chi nhánh để xem đề xuất của bot.'}
                      </div>
                    )}

                    {!botOptionsCollapsed && botProposals.length > 0 && (
                      <>
                        <div className="mb-2 d-flex flex-wrap gap-2">
                          {botProposals.map((option) => (
                            <Button
                              key={option.id}
                              size="sm"
                              variant={selectedProposalId === option.id ? 'primary' : 'outline-primary'}
                              title={
                                option.difference_label ||
                                (option.dropped_factors?.length > 0
                                  ? `Bỏ: ${option.dropped_factors
                                      .map((key) => BOT_FACTOR_SHORT_LABEL[key] || key)
                                      .join(', ')}`
                                  : 'Dùng đủ bộ yếu tố ưu tiên đã chốt')
                              }
                              disabled={applyProposalLoading}
                              // #182918 — chạm 1 lần = xem số, chạm đúp = áp dụng; không cho
                              // trình duyệt điện thoại hiểu chạm đúp là phóng to màn.
                              style={{ touchAction: 'manipulation' }}
                              data-testid="bot-option-button"
                              onClick={() => handleProposalTap(option)}
                            >
                              {option.name}
                            </Button>
                          ))}
                        </div>

                        {/* #182918 — bỏ 4 dòng chữ giữa hàng nút và thẻ số (điểm khác / yếu tố bị
                            lược / lý do thiếu phương án / mức đáp ứng từng yếu tố) theo góp ý Trợ
                            năng; "điểm khác / bỏ yếu tố nào" vẫn nằm ở chú thích (title) của nút. */}
                        {selectedProposal && (
                          <div className="mb-3 d-flex flex-column gap-2">
                            <div className="d-flex flex-wrap gap-2">
                              <Badge bg="success">
                                Duyệt được (đề xuất): {selectedProposal.stats?.filledSlots ?? 0}
                              </Badge>
                              <Badge
                                bg="warning"
                                text="dark"
                              >
                                Còn trống trong slot có đơn: {selectedProposal.stats?.emptySlots ?? 0}
                              </Badge>
                              <Badge bg={stillMissingShifts > 0 ? 'danger' : 'info'}>
                                Ca trống tuần tới (còn thiếu người): {stillMissingShifts}
                              </Badge>
                            </div>
                            {/* #125027 — góp ý Trợ năng: NGAY DƯỚI 3 thẻ số phải thấy TÊN từng nhân
                                viên kèm (đề xuất bao nhiêu ca, đăng ký bao nhiêu ca) của tuần đang
                                xem, không phải cuộn xuống cuối tab. Hiện ở CẢ 2 kiểu xem. */}
                            {proposedEmployeeRows.length > 0 && (
                              <div
                                className="d-flex flex-column gap-1"
                                data-testid="bot-employee-shift-counts"
                              >
                                <div className="small fw-semibold">Đề xuất / đăng ký theo nhân viên</div>
                                <div className="d-flex flex-wrap gap-1">
                                  {proposedEmployeeRows.map((row) => (
                                    <Badge
                                      key={row.id}
                                      bg="light"
                                      text="dark"
                                      className="border small text-wrap text-start"
                                      style={{ whiteSpace: 'normal' }}
                                      title={`${row.name} — đề xuất ${row.proposed} ca, đăng ký ${row.registered} ca`}
                                    >
                                      {row.name}: đề xuất <strong>{row.proposed}</strong> ca · đăng ký{' '}
                                      <strong>{row.registered}</strong> ca
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* #011541 (góp ý Trợ năng: kiểu xem Lịch tuần chỉ giữ hàng nút +
                                các thẻ số + khung lịch tuần) — phần diễn giải/gợi ý/danh sách
                                mang qua từ kiểu xem Danh sách chỉ hiện ở Danh sách. */}
                            {botViewMode === 'list' && (
                              <>
                                <div className="small text-muted">
                                  "Ca trống tuần tới" = tổng nhu cầu nhân sự các slot chưa có người ({calendarDemandUncovered}
                                  ) - số ca bot duyệt được ({selectedProposal.stats?.filledSlots ?? 0}) ={' '}
                                  <strong>{stillMissingShifts}</strong> ca còn thiếu (không dùng trung bình tuần trước).
                                </div>
                                {stillMissingShifts > 0 && (
                                  <div className="small text-danger">
                                    ⚠️ Sau khi áp phương án này vẫn còn <strong>{stillMissingShifts}</strong> ca chưa có ai
                                    nhận — cần tuyển thêm / mở boost thưởng cho các slot trống.
                                  </div>
                                )}
                                <div className="small text-muted">
                                  "Còn trống trong slot có đơn" chỉ đếm phần thiếu ở những slot ĐÃ có nhân viên đăng ký; slot
                                  không ai đăng ký nằm ở "Ca trống tuần tới".
                                </div>
                                <div className="small text-muted">
                                  Phân bổ ca:{' '}
                                  {(selectedProposal.stats?.shiftsPerEmployee || [])
                                    .slice(0, 8)
                                    .map((x) => `${x.employee_name || x.employee_id}(${x.count ?? x.shift_count ?? 0})`)
                                    .join(', ') || 'N/A'}
                                </div>
                                {floorSuggestionLoading ? (
                                  <div className="small text-muted">Đang phân tích gợi ý mốc tối thiểu 4 ca...</div>
                                ) : floorSuggestionData ? (
                                  <div className="rounded border bg-white p-2">
                                    <div className="small fw-semibold mb-1">
                                      Gợi ý tối ưu floor {floorSuggestionData.min_shifts ?? 4} ca (
                                      {floorSuggestionData.suggestions?.length || 0} gợi ý)
                                    </div>
                                    <div className="small text-muted mb-1">
                                      Thiếu hụt dự kiến:{' '}
                                      {floorSuggestionData.summary?.projected_deficit_total ?? 0} → sau tối ưu:{' '}
                                      {floorSuggestionData.summary?.optimized_deficit_total ?? 0}
                                    </div>
                                    <div className="small text-muted">
                                      {(floorSuggestionData.suggestions || [])
                                        .slice(0, 3)
                                        .map((item) => {
                                          const start = moment(item.start_time).format('ddd DD/MM HH:mm');
                                          return `${start}: ${item.from_employee_name || item.from_employee_id} → ${item.to_employee_name || item.to_employee_id}`;
                                        })
                                        .join(' | ') || 'Chưa có gợi ý đổi slot nào phù hợp.'}
                                    </div>
                                  </div>
                                ) : null}
                                {proposalRevenueMeta?.enabled ? (
                                  <div className="small text-muted">
                                    {/* #130536 — "ca đông" nay do quản lý gắn cờ tay, không còn tự tính top 30%. */}
                                    Luật doanh thu đang bật: "ca đông" là{' '}
                                    {proposalRevenueMeta.high_revenue_flagged_count ?? 0} ca ĐƯỢC GẮN CỜ THỦ CÔNG (không
                                    còn tự tính theo % doanh thu); doanh thu hiển thị tính trên{' '}
                                    {proposalRevenueMeta.lookback_weeks} tuần gần nhất; NV dưới{' '}
                                    {proposalRevenueMeta.new_employee_days} ngày là NV mới
                                    {proposalRevenueMeta.block_new_in_high_revenue
                                      ? ' — bot KHÔNG tự đề xuất NV mới vào ca đông, quản lý duyệt tay.'
                                      : ' — bot vẫn tự đề xuất, chỉ cảnh báo.'}
                                    {' '}
                                    <a href="/shift-revenue-ranking">Gắn cờ ca đông →</a>
                                  </div>
                                ) : (
                                  <div className="small text-muted">
                                    Luật ưu tiên theo doanh thu đang TẮT — bot xếp ca theo luật cũ.
                                  </div>
                                )}
                                {newHireCashierExcluded.length > 0 && (
                                  <div className="rounded border border-secondary bg-white p-2">
                                    <div className="small fw-semibold mb-1">
                                      🔒 {newHireCashierExcluded.length} đăng ký ca chính bot KHÔNG đề xuất — NV chưa đủ
                                      ca tập việc
                                    </div>
                                    <div className="d-flex flex-column gap-1">
                                      {newHireCashierExcluded.slice(0, 10).map((ex) => (
                                        <div key={`nh-${ex.shift_id}`} className="small text-muted">
                                          {moment(ex.start_time).format('ddd DD/MM HH:mm')} —{' '}
                                          {ex.template_name || ex.template_id} — {ex.employee_name || ex.employee_id}
                                        </div>
                                      ))}
                                    </div>
                                    <div className="small text-muted mt-1">
                                      Chỉ BOT không đề xuất những ca này — các đăng ký vẫn nằm ở mục chờ duyệt và bạn
                                      DUYỆT TAY được bình thường nếu muốn cho NV đứng quầy.
                                    </div>
                                  </div>
                                )}
                                {manualReviewItems.length > 0 && (
                                  <div className="rounded border border-warning bg-white p-2">
                                    <div className="small fw-semibold mb-1 text-danger">
                                      ⚠️ {manualReviewItems.length} đăng ký bot KHÔNG tự đề xuất — cần quản lý quyết
                                    </div>
                                    <div className="d-flex flex-column gap-1">
                                      {manualReviewItems.slice(0, 10).map((mi) => (
                                        <div
                                          key={`${mi.shift_id}`}
                                          className="small text-muted"
                                        >
                                          {moment(mi.start_time).format('ddd DD/MM HH:mm')} —{' '}
                                          {mi.template_name || mi.template_id} — {mi.employee_name || mi.employee_id} (
                                          {formatVnd(mi.drink_revenue)} doanh thu nước): {mi.blocked_reason}
                                        </div>
                                      ))}
                                    </div>
                                    <div className="small text-muted mt-1">
                                      Các ca này vẫn nằm ở tab "Chờ duyệt" — quản lý duyệt tay nếu muốn xếp người vào.
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* #212630 — hiện ĐỦ mọi ca của đề xuất đang chọn ở CẢ 2 kiểu xem
                            (trước đây chỉ hiện ở kiểu xem Danh sách và cắt còn 20 dòng đầu). */}
                        {selectedProposalItems.length > 0 && (
                          <div className="mb-1 small fw-semibold">
                            Ca đã được đề xuất ({selectedProposalItems.length})
                          </div>
                        )}
                        {selectedProposalItems.length > 0 && (
                          <div className="d-flex flex-column gap-2">
                            {selectedProposalItems.map((item) => (
                              <div
                                key={`${item.slot_key}-${item.shift_id}`}
                                className="rounded border bg-white p-2"
                              >
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                  <div>
                                    <div className="fw-semibold">
                                      {item.template_name || item.template_id} —{' '}
                                      {moment(item.start_time).format('ddd DD/MM HH:mm')}
                                    </div>
                                    <div className="small text-muted">{item.branch_name || ''}</div>
                                    <div className="mt-1 d-flex flex-wrap gap-1 align-items-center">
                                      {renderShiftTierBadge(item)}
                                      {item.employee_drink_revenue_rank ? (
                                        <Badge bg="info">
                                          NV hạng {item.employee_drink_revenue_rank} doanh thu nước —{' '}
                                          {formatVnd(item.employee_drink_revenue)}
                                        </Badge>
                                      ) : null}
                                      {item.is_new_employee ? (
                                        <Badge
                                          bg="warning"
                                          text="dark"
                                        >
                                          NV mới
                                        </Badge>
                                      ) : null}
                                    </div>
                                  </div>
                                  <Form.Select
                                    size="sm"
                                    style={{ maxWidth: '320px' }}
                                    value={
                                      proposalItemsOverride[proposalOverrideKey(item)] ??
                                      proposalItemsOverride[item.slot_key] ??
                                      item.employee_id
                                    }
                                    onChange={(e) => {
                                      const k = proposalOverrideKey(item);
                                      setProposalItemsOverride((prev) => ({ ...prev, [k]: e.target.value }));
                                    }}
                                  >
                                    {(item.candidates || []).map((c) => (
                                      <option
                                        key={c.shift_id}
                                        value={c.employee_id}
                                      >
                                        {c.employee_name || c.employee_id}
                                        {c.is_preferred ? ' ⭐' : ''}
                                        {c.drink_revenue_rank ? ` — nước #${c.drink_revenue_rank}` : ''}
                                        {c.blocked_reason ? ' ⚠️ cần duyệt tay' : ''}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </div>
                                {item.bot_reason ? (
                                  <div className="small text-muted mt-1">Lý do bot chọn: {item.bot_reason}</div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* #225256 — DƯỚI danh sách ca đã được đề xuất, hiện thêm các CA CÒN THIẾU
                            NGƯỜI (sau khi áp phương án đang chọn) dạng chip gọn: tiết kiệm diện
                            tích nhưng vẫn đủ thông tin ca (thiếu mấy người · tên ca · ngày giờ ·
                            boost). Chỉ ở kiểu xem Danh sách vì Lịch tuần đã hiện badge
                            "Thiếu N người" theo TỪNG NGÀY (#180848) — không lặp 2 lần (#011541). */}
                        {botViewMode === 'list' && missingShiftRows.length > 0 && (
                          <div className="mt-3" data-testid="bot-missing-shifts">
                            <div className="small fw-semibold mb-1">
                              Ca còn thiếu người ({missingShiftRows.length})
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {missingShiftRows.map((row) => (
                                <Badge
                                  key={`missing-${row.template_id}-${row.start_time}`}
                                  bg="light"
                                  text="dark"
                                  className="border small text-wrap text-start"
                                  style={{ whiteSpace: 'normal' }}
                                  title="Slot của lịch tuần vẫn chưa đủ người sau khi áp phương án đang chọn"
                                  data-testid={`bot-missing-shift-${row.template_id}-${row.start_time}`}
                                >
                                  <span className="fw-semibold text-danger">Thiếu {row.still_missing}</span>
                                  {' · '}
                                  {row.template_name}
                                  {' · '}
                                  {moment(row.start_time).format('ddd DD/MM HH:mm')}-
                                  {moment(row.end_time).format('HH:mm')}
                                  {row.has_boost ? ` · ⚡ ${row.boost_reward_rate_bonus}%` : ''}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {botViewMode === 'week' &&
                    (botShiftsLoading ? (
                      <div className="text-center py-5 text-muted">Đang tải...</div>
                    ) : botShifts.length === 0 ? (
                      <div
                        className="text-center py-5 text-muted"
                        data-testid="bot-week-empty"
                      >
                        <Robot
                          size={40}
                          className="mb-3 text-muted"
                        />
                        <div>Chưa có đề xuất cho tuần này</div>
                        <div className="small mt-1">
                          Bấm "Chạy lại Auto Approve" để bot xét lại ca chờ duyệt của tuần này, hoặc chuyển sang
                          tuần khác.
                        </div>
                      </div>
                    ) : (
                      <Row data-testid="bot-week-view">
                        {renderWeekView({
                          weekStart: botWeekInfo.start.toDate(),
                          shiftsForDay: getProposedShiftsForDate,
                          // Thẻ NGÀY ở đây không mở modal chi tiết ngày như tab Lịch ca: bấm vào
                          // 1 CA mới là hành động có nghĩa (sửa tay đúng ca đó).
                          onDayClick: null,
                          emptyDayText: 'Không có đề xuất',
                          onShiftClick: (s) => {
                            setEditingProposedShift(s.raw);
                            setShowEditProposedModal(true);
                          },
                          // #180848 — nói RÕ ngày nào/ca nào còn thiếu mấy người sau khi áp
                          // phương án đang chọn, tái dùng đúng khuôn badge "Thiếu N người" của
                          // tab "Ca trống" thay vì chỉ 1 con số tổng ở badge phía trên.
                          dayExtra: (dateStr) => {
                            const missing = missingSlotsByDate[dateStr];
                            if (!missing?.length) return null;
                            return (
                              <div
                                className="mt-2 d-flex flex-wrap gap-2"
                                data-testid={`bot-week-missing-${dateStr}`}
                              >
                                {missing.map((slot) => (
                                  <Badge
                                    key={`${slot.template_id}-${slot.start_time}`}
                                    bg="danger"
                                    className="d-flex flex-wrap align-items-center gap-1 p-2 text-wrap text-start mw-100"
                                    style={{ whiteSpace: 'normal', minWidth: 0 }}
                                    title="Slot của lịch tuần vẫn chưa đủ người sau khi áp phương án đang chọn"
                                  >
                                    <span className="fw-bold text-break">Thiếu {slot.still_missing} người</span>
                                    <span className="small text-break">{slot.template_name}</span>
                                    <span className="small text-break">
                                      {moment(slot.start_time).format('HH:mm')} -{' '}
                                      {moment(slot.end_time).format('HH:mm')}
                                    </span>
                                    {slot.has_boost ? (
                                      <Badge
                                        bg="warning"
                                        text="dark"
                                        className="small"
                                      >
                                        ⚡ Boost {slot.boost_reward_rate_bonus}%
                                      </Badge>
                                    ) : null}
                                  </Badge>
                                ))}
                              </div>
                            );
                          },
                          // #001848 — dải tên NGƯỜI CÙNG ĐĂNG KÝ ca này, ngay dưới ca, chữ 30%
                          // cỡ chữ thường để chiếm ít chỗ nhất; bấm 1 tên là đổi đề xuất luôn.
                          shiftBelow: (s) =>
                            s.registrants.length > 0 ? (
                              <div
                                data-testid={`week-shift-registrants-${s.id}`}
                                className="d-flex flex-wrap align-items-center column-gap-1 mw-100"
                                style={{ fontSize: '30%', lineHeight: 1.2, minWidth: 0 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {s.registrants.map((emp) => (
                                  <button
                                    key={emp.id}
                                    type="button"
                                    className="btn btn-link p-0 text-decoration-none align-baseline"
                                    // textTransform: nút .btn của giao diện này viết HOA sẵn — chữ
                                    // hoa rộng hơn, đi ngược đúng mục đích "tiết kiệm diện tích".
                                    style={{ fontSize: 'inherit', lineHeight: 'inherit', textTransform: 'none' }}
                                    title={`Đổi đề xuất sang ${emp.full_name}`}
                                    disabled={loading || quickSwapShiftId === s.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickSwapProposedEmployee(s, emp);
                                    }}
                                  >
                                    {getShortName(emp.full_name)}
                                  </button>
                                ))}
                              </div>
                            ) : null,
                          shiftExtra: (s) => (
                            <Button
                              data-testid={`week-shift-reject-${s.id}`}
                              size="sm"
                              variant="outline-danger"
                              className="py-0 px-1"
                              title="Từ chối ca này — bỏ khỏi lô đề xuất, không tính vào Duyệt tất cả"
                              disabled={loading}
                              onClick={async (e) => {
                                e.stopPropagation();
                                // #195455 — chỉ xoá khung khi từ chối THÀNH CÔNG (thất bại thì CSDL không đổi).
                                if (await handleRejectShift(s.id)) setAppliedProposalResult(null);
                                await loadBotShifts();
                              }}
                            >
                              <XCircle size={12} />
                            </Button>
                          ),
                        })}
                      </Row>
                    ))}

                  {botViewMode === 'list' && (
                  <>
                  {botShiftsLoading ? (
                    <div className="text-center py-5 text-muted">Đang tải...</div>
                  ) : botShifts.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <Robot
                        size={40}
                        className="mb-3 text-muted"
                      />
                      <div>Bot chưa đề xuất ca nào cho tuần tới.</div>
                      <div className="small mt-1">Nhấn "Chạy lại Auto Approve" để bot xét lại ca PENDING tuần tới.</div>
                    </div>
                  ) : (
                    (() => {
                      // Group by (template_name, start_time)
                      const grouped = {};
                      [...botShifts]
                        .sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf())
                        .forEach((s) => {
                          const key = `${s.template_name || s.template_id}__${s.start_time}`;
                          if (!grouped[key]) {
                            grouped[key] = {
                              template_name: s.template_name || 'Không rõ template',
                              start_time: s.start_time,
                              end_time: s.end_time,
                              branch_name: s.branch_name || '',
                              shifts: [],
                            };
                          }
                          grouped[key].shifts.push(s);
                        });

                      const sortedGroups = Object.values(grouped)
                        .map((group) => ({
                          ...group,
                          shifts: [...group.shifts].sort((a, b) =>
                            (a.employee_name || '').localeCompare(b.employee_name || ''),
                          ),
                        }))
                        .sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());

                      return (
                        <div className="d-flex flex-column gap-3">
                          {sortedGroups.map((group, gi) => (
                            <Card
                              key={gi}
                              className="border-info"
                            >
                              <Card.Header className="bg-light">
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg="info">🤖 Bot</Badge>
                                  <strong>{group.template_name}</strong>
                                  <span className="text-muted small">
                                    {moment(group.start_time).format('ddd DD/MM HH:mm')} –{' '}
                                    {moment(group.end_time).format('HH:mm')}
                                  </span>
                                  {group.branch_name && (
                                    <Badge
                                      bg="secondary"
                                      className="small"
                                    >
                                      {group.branch_name}
                                    </Badge>
                                  )}
                                  <Badge
                                    bg="light"
                                    text="dark"
                                    className="small"
                                  >
                                    {group.shifts.length} nhân viên
                                  </Badge>
                                </div>
                              </Card.Header>
                              <Card.Body className="py-2">
                                <div className="d-flex flex-column gap-2">
                                  {group.shifts.map((shift) => (
                                    <Row
                                      key={shift.id}
                                      className="align-items-center py-1 border-bottom"
                                    >
                                      <Col>
                                        <div className="fw-bold">
                                          {shift.employee_name || 'Chưa xác định'}
                                          {shift.is_preferred && (
                                            <Badge
                                              bg="warning"
                                              text="dark"
                                              className="ms-2 small"
                                            >
                                              ⭐ Ưu tiên
                                            </Badge>
                                          )}
                                          <NewHireCashierBadge
                                            shift={shift}
                                            className="ms-2"
                                          />
                                        </div>
                                        <div className="text-muted small">
                                          {shift.approved_shifts > 0 && `${shift.approved_shifts} ca đã duyệt tuần này`}
                                        </div>
                                      </Col>
                                      <Col xs="auto">
                                        <div className="d-flex gap-1">
                                          <Button
                                            size="sm"
                                            variant="success"
                                            onClick={async () => {
                                              // #195455 — chỉ xoá khung khi thao tác THÀNH CÔNG.
                                              if (await handleApproveShift(shift.id)) setAppliedProposalResult(null);
                                              loadBotShifts();
                                            }}
                                            disabled={loading}
                                            title="Xác nhận duyệt chính thức"
                                          >
                                            <CheckCircle
                                              size={14}
                                              className="me-1"
                                            />
                                            Duyệt
                                          </Button>
                                          {/* #195455 — máy chủ chỉ cho huỷ duyệt ca APPROVED/APPROVE_BOT; ca
                                              "Đề xuất — chưa duyệt" (PROPOSED_BOT) bấm Revoke luôn bị trả 400 nên ẩn nút. */}
                                          {(shift.status === 'APPROVED' || shift.status === 'APPROVE_BOT') && (
                                            <Button
                                              size="sm"
                                              variant="outline-warning"
                                              onClick={async () => {
                                                // #195455 — chỉ xoá khung khi thao tác THÀNH CÔNG.
                                                if (await handleRevokeApproval(shift.id)) setAppliedProposalResult(null);
                                                loadBotShifts();
                                              }}
                                              disabled={loading}
                                              title="Hủy duyệt bot, trả về PENDING"
                                            >
                                              <ArrowClockwise
                                                size={14}
                                                className="me-1"
                                              />
                                              Revoke
                                            </Button>
                                          )}
                                          <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={async () => {
                                              // #195455 — chỉ xoá khung khi thao tác THÀNH CÔNG.
                                              if (await handleRejectShift(shift.id)) setAppliedProposalResult(null);
                                              loadBotShifts();
                                            }}
                                            disabled={loading}
                                            title="Từ chối ca"
                                          >
                                            <XCircle
                                              size={14}
                                              className="me-1"
                                            />
                                            Reject
                                          </Button>
                                        </div>
                                      </Col>
                                    </Row>
                                  ))}
                                </div>
                              </Card.Body>
                            </Card>
                          ))}
                        </div>
                      );
                    })()
                  )}
                  </>
                  )}

                  {/* #172346 — khối "Đã đề xuất bao nhiêu ca" nay hiện NGAY DƯỚI danh sách
                      đề xuất (trước đây nằm trên đầu tab, che mất phần đề xuất). */}
                  {proposedCountRows.length > 0 && (
                    <div
                      className="mt-3 rounded border bg-light p-2"
                      data-testid="bot-proposed-counts"
                    >
                      <div className="small fw-semibold mb-1">Đã đề xuất bao nhiêu ca — {botWeekInfo.label}</div>
                      <div className="d-flex flex-wrap gap-1">
                        {proposedCountRows.map((row) => (
                          <Badge
                            key={row.id}
                            bg="light"
                            text="dark"
                            className="border small text-wrap text-start"
                            style={{ whiteSpace: 'normal' }}
                            title={row.name}
                          >
                            {row.name}: <strong>{row.count}</strong> ca
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* ── Tab: Xếp hạng đóng góp ca (#131031) ───────────────────── */}
            <Tab.Pane eventKey="leaderboard">
              <ShiftLeaderboardTab
                isActive={activeTab === 'leaderboard'}
                branchId={selectedBranch?.id}
                onCloseWeek={isAdminUser ? setCloseWeekTarget : undefined}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      {rejectReasonModal}

      <WeekCloseModal
        show={closeWeekTarget !== null}
        onHide={() => setCloseWeekTarget(null)}
        branchId={selectedBranch?.id}
        weekStart={closeWeekTarget || undefined}
      />

      {/* Modal hiển thị chi tiết ca trong ngày (component tách riêng) */}
      <ShiftDayDetailModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        selectedDate={selectedDate}
        shiftsForDate={selectedDateShifts}
        onApproveShift={handleApproveShift}
        onRejectShift={handleRejectShift}
        onRevokeApproval={handleRevokeApproval}
        onUndoRejectShift={handleUndoRejectShift}
        loading={loading}
        mode={viewMode}
        onRefreshAfterBulk={() => {
          // Sau bulk action, reload data theo viewMode đang xem
          const { from, to } = getDateRange();
          withBranch(shiftAPI.getShifts, {
            date_from: from,
            date_to: to,
            page: 1,
            limit: 1000,
            status: statusFilter || '',
          }).then((shiftsResponse) => {
            const mapped = (shiftsResponse.data || [])
              .map((s) => {
                const date = moment(s.start_time).format('YYYY-MM-DD');
                const time = `Ca ${moment(s.start_time).format('HH:mm')}`;
                const start_ts = moment(s.start_time).valueOf();
                return {
                  id: s.id,
                  date,
                  type: s.shift_type || 'NORMAL',
                  time,
                  status: s.status,
                  employee_id: s.employee_id,
                  employee_name: s.employee_name || 'Chưa xác định',
                  template_id: s.template_id,
                  template_name: s.template_name || s.shift_type,
                  start_ts,
                  is_preferred: s.is_preferred || false,
                };
              })
              .sort((a, b) => {
                // Sắp xếp: ca ưu tiên lên đầu, sau đó theo thời gian
                if (a.is_preferred !== b.is_preferred) {
                  return b.is_preferred ? 1 : -1;
                }
                return a.start_ts - b.start_ts;
              });
            setShifts(mapped);
            const pending = mapped.filter((s) => s.status === 'PENDING');
            setPendingShifts(pending);
          });
        }}
      />

      {/* #015110 — Modal sửa TAY 1 ca đề xuất ngay trên bảng lịch tuần */}
      <EditProposedShiftModal
        show={showEditProposedModal}
        onHide={() => {
          setShowEditProposedModal(false);
          setEditingProposedShift(null);
        }}
        shift={editingProposedShift}
        proposedCountByEmployee={proposedCountByEmployee}
        templates={templates}
        onSaved={async () => {
          setShowEditProposedModal(false);
          setEditingProposedShift(null);
          toast.success('Đã lưu thay đổi — ca vẫn là đề xuất chưa duyệt');
          setAppliedProposalResult(null); // #191002
          await loadBotShifts();
        }}
      />

      {/* Modal sửa ca đã duyệt */}
      <EditApprovedShiftModal
        show={showEditApprovedModal}
        onHide={() => {
          setShowEditApprovedModal(false);
          setEditingApprovedShift(null);
        }}
        shift={editingApprovedShift}
        templates={templates}
        saving={savingEdit}
        onSave={async (shiftId, data) => {
          try {
            setSavingEdit(true);
            await shiftAPI.updateShift(shiftId, data);
            // reload tháng hiện tại
            const from = moment(currentDate).startOf('month').format('YYYY-MM-DD');
            const to = moment(currentDate).endOf('month').format('YYYY-MM-DD');
            const shiftsResponse = await withBranch(shiftAPI.getShifts, {
              date_from: from,
              date_to: to,
              page: 1,
              limit: 1000,
              status: statusFilter || '',
            });
            const mapped = (shiftsResponse.data || [])
              .map((s) => {
                const date = moment(s.start_time).format('YYYY-MM-DD');
                const time = `Ca ${moment(s.start_time).format('HH:mm')}`;
                const start_ts = moment(s.start_time).valueOf();
                return {
                  id: s.id,
                  date,
                  type: s.shift_type || 'NORMAL',
                  time,
                  status: s.status,
                  employee_id: s.employee_id,
                  employee_name: s.employee_name || 'Chưa xác định',
                  template_id: s.template_id,
                  template_name: s.template_name || s.shift_type,
                  start_ts,
                  is_preferred: s.is_preferred || false,
                };
              })
              .sort((a, b) => {
                // Sắp xếp: ca ưu tiên lên đầu, sau đó theo thời gian
                if (a.is_preferred !== b.is_preferred) {
                  return b.is_preferred ? 1 : -1;
                }
                return a.start_ts - b.start_ts;
              });
            setShifts(mapped);
            setPendingShifts(mapped.filter((s) => s.status === 'PENDING'));
            setShowEditApprovedModal(false);
            setEditingApprovedShift(null);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
          } finally {
            setSavingEdit(false);
          }
        }}
      />

      {/* Modal Set Boost */}
      <Modal
        show={showBoostModal}
        onHide={() => {
          setShowBoostModal(false);
          setSelectedSlotForBoost(null);
          setBoostRewardRate(0);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Boost Thưởng</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSlotForBoost && (
            <>
              <div className="mb-3">
                <strong>Ca:</strong> {selectedSlotForBoost.template_name}
              </div>
              <div className="mb-3">
                <strong>Ngày:</strong> {moment(selectedSlotForBoost.date).format('dddd, DD/MM/YYYY')}
              </div>
              <div className="mb-3">
                <strong>Thời gian:</strong> {moment(selectedSlotForBoost.start_time).format('HH:mm')} -{' '}
                {moment(selectedSlotForBoost.end_time).format('HH:mm')}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Tỉ lệ thưởng boost (%) *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={boostRewardRate}
                  onChange={(e) => setBoostRewardRate(parseInt(e.target.value) || 0)}
                  placeholder="Nhập tỉ lệ thưởng (0-100)"
                />
                <Form.Text className="text-muted">
                  Tỉ lệ thưởng tăng thêm để khuyến khích nhân viên đăng ký ca này
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowBoostModal(false);
              setSelectedSlotForBoost(null);
              setBoostRewardRate(0);
            }}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleSetBoost}
            disabled={loading || boostRewardRate < 0}
          >
            {loading ? 'Đang xử lý...' : selectedSlotForBoost?.has_boost ? 'Cập nhật' : 'Set boost'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
